import { PATH_DASHBOARD } from "../routes/paths";
import useAuth from "../hooks/useAuth";
import { MasterCardIcon, VisaIcon } from "../assets";
import {
  format,
  isAfter,
  isBefore,
  isSameDay,
  isWithinInterval,
  parse,
  parseISO,
} from "date-fns";
import { fInterval } from "./formatTime";
import { isNumeric } from "./formatNumber";

function path(root, sublink) {
  return `${root}${sublink}`;
}

const ROOTS_API = "https://api.useafritas.com/app/";
const ROOTS_ASSETS = "https://assets.useafritas.com/";

export const API_PATH = {
  root: ROOTS_API,
  dashboard: path(ROOTS_API, "dashboard"),
  bookings: path(ROOTS_API, "bookings"),
  listings: path(ROOTS_API, "listings"),
  leads: path(ROOTS_API, "leads"),
  properties: path(ROOTS_API, "properties"),
  automobiles: path(ROOTS_API, "automobiles"),
  boats: path(ROOTS_API, "boats"),
  events: path(ROOTS_API, "events"),
  login: path(ROOTS_API, "login"),
  verify: path(ROOTS_API, "verify"),
  forgot: path(ROOTS_API, "forgot-password"),
  profile: path(ROOTS_API, "profile"),
  wallet: path(ROOTS_API, "wallet"),
  settings: path(ROOTS_API, "settings"),
  referrals: path(ROOTS_API, "referrals"),
  signup: path(ROOTS_API, "signup"),
  misc: path(ROOTS_API, "misc"),
  log: path(ROOTS_API, "logs"),
  earnings: path(ROOTS_API, "earnings"),
  transactions: path(ROOTS_API, "transactions"),
  reviews: path(ROOTS_API, "reviews"),
  payment: path(ROOTS_API, "payment"),
  support: path(ROOTS_API, "support"),
  insights: path(ROOTS_API, "insights"),
  messages: path(ROOTS_API, "messages"),
  staff: path(ROOTS_API, "staff"),
  role: path(ROOTS_API, "roles"),
  calendar: path(ROOTS_API, "calendar"),
  billPayments: path(ROOTS_API, "bill-payments"),
  subscription: path(ROOTS_API, "subscriptions"),
  terminals: path(ROOTS_API, "terminals"),
  reports: path(ROOTS_API, "reports"),
};
export const SUBSCRIPTION_STATUS = [
  { label: "Inactive", color: "warning" },
  { label: "Active", color: "success" },
  { label: "Cancelled", color: "error" },
];
export const TERMINAL_REQUEST_STATUS = [
  { label: "Pending", color: "warning", value: "0" },
  { label: "Delivery In Progress", color: "info", value: "1" },
  { label: "Delivered", color: "success", value: "2" },
  { label: "Refunded", color: "error", value: "3" },
];
export const TERMINAL_STATUS = [
  { label: "Disconnected", color: "error", value: "0" },
  { label: "Linked", color: "success", value: "1" },
];
export const TERMINAL_DEVICE_STATUS = [
  { label: "Offline", color: "error", value: "0" },
  { label: "Online & Available", color: "success", value: "1" },
  { label: "Available & Offline", color: "success", value: "2" },
];
export const GENDERS = [
  { label: "Male", value: "M" },
  { label: "Female", value: "F" },
];
export const REVIEW_CATEGORIES = [
  { name: "Cleanliness", value: "cleanliness" },
  { name: "Location", value: "location" },
  { name: "Facilities", value: "facilities" },
  { name: "Value for Money", value: "value" },
  { name: "Comfort", value: "comfort" },
  { name: "Accuracy", value: "accuracy" },
  { name: "Communication", value: "communication" },
  { name: "Free Wifi", value: "wifi" },
];
export const REVIEW_AUTOMOBILE_CATEGORIES = [
  { name: "Cleanliness", value: "cleanliness" },
  { name: "Location", value: "location" },
  { name: "Value for Money", value: "value" },
  { name: "Car Condition", value: "condition" },
  { name: "Reservation Process", value: "reservation" },
  { name: "Customer Service", value: "customer" },
  { name: "Pick-up & Drop-off Experience", value: "pickup" },
  { name: "Helpfulness", value: "helpfulness" },
];
export const ASSETS_PATH = {
  listings: path(ROOTS_ASSETS, "listings/"),
  events: path(ROOTS_ASSETS, "events/"),
  users: path(ROOTS_ASSETS, "users/"),
  chats: path(ROOTS_ASSETS, "chats/"),
  reviews: path(ROOTS_ASSETS, "reviews/"),
  customers: path(ROOTS_ASSETS, "users/"),
  resources: path(ROOTS_ASSETS, "posts/"),
  properties: path(ROOTS_ASSETS, "properties/"),
  boats: path(ROOTS_ASSETS, "boats/"),
  vehicles: path(ROOTS_ASSETS, "automobiles/"),
  attachments: path(ROOTS_ASSETS, "attachments/"),
  proofs: path(ROOTS_ASSETS, "proofs/"),
};
export const PAGE_TITLES = {
  [PATH_DASHBOARD.general.root]: "Dashboard",
  [PATH_DASHBOARD.general.settings]: "Settings",
  [PATH_DASHBOARD.bookings]: "Bookings",
  [PATH_DASHBOARD.addBookings]: "Booking",
  [PATH_DASHBOARD.listings]: "Listing",
  [PATH_DASHBOARD.earnings]: "Earnings",
  [PATH_DASHBOARD.reviews]: "Reviews",
  [PATH_DASHBOARD.insights]: "Insights",
  [PATH_DASHBOARD.messages]: "Messages",
  [PATH_DASHBOARD.tickets]: "Tickets",
  [PATH_DASHBOARD.addTicket]: "Ticket",
  [PATH_DASHBOARD.ticketTypes]: "Ticket",
  [PATH_DASHBOARD.checkedIn]: "Checked In",
  [PATH_DASHBOARD.leads]: "Leads",
  [PATH_DASHBOARD.wallet]: "Wallet",
  [PATH_DASHBOARD.staff]: "Staff",
  [PATH_DASHBOARD.roles]: "Staff Roles",
  [PATH_DASHBOARD.billPayments]: "Bill Payments",
};
export const IMAGE_LINKS = [
  ASSETS_PATH.listings,
  ASSETS_PATH.events,
  ASSETS_PATH.vehicles,
  ASSETS_PATH.listings,
  ASSETS_PATH.boats,
  ASSETS_PATH.properties,
  ASSETS_PATH.events,
  ASSETS_PATH.reviews,
];

export const CALENDAR_CUSTOM_TYPES = [
  "Custom Weekend Price",
  "Custom Weekly Price",
  "Custom Monthly Price",
];
export const STATES = [
  "Abia",
  "Adamawa",
  "Akwa Ibom",
  "Anambra",
  "Bauchi",
  "Bayelsa",
  "Benue",
  "Borno",
  "Cross River",
  "Delta",
  "Ebonyi",
  "Edo",
  "Ekiti",
  "Enugu",
  "FCT - Abuja",
  "Gombe",
  "Imo",
  "Jigawa",
  "Kaduna",
  "Kano",
  "Katsina",
  "Kebbi",
  "Kogi",
  "Kwara",
  "Lagos",
  "Nasarawa",
  "Niger",
  "Ogun",
  "Ondo",
  "Osun",
  "Oyo",
  "Plateau",
  "Rivers",
  "Sokoto",
  "Taraba",
  "Yobe",
  "Zamfara",
];
export const BUSINESS_SWITCHES = [0, 3, 1, 6];
export const MONTHS = [
  { label: "January", value: "1" },
  { label: "February", value: "2" },
  { label: "March", value: "3" },
  { label: "April", value: "4" },
  { label: "May", value: "5" },
  { label: "June", value: "6" },
  { label: "July", value: "7" },
  { label: "August", value: "8" },
  { label: "September", value: "9" },
  { label: "October", value: "10" },
  { label: "November", value: "11" },
  { label: "December", value: "12" },
];
export const INSIGHTS = [
  {
    title: "Search Window",
    subtitle: "Most searches were looking for 8 - 30 days in the future",
    key: "search",
    icon: "proicons:calendar",
  },
  {
    title: "Traveller type",
    subtitle: "Most searches were looking for 8 - 30 days in the future",
    key: "search",
    icon: "proicons:calendar",
  },
];
export const COUNTRIES = [
  { code: "AD", name: "Andorra", dial_code: "+376", phoneLength: 6 },
  {
    code: "AE",
    name: "United Arab Emirates",
    dial_code: "+971",
    phoneLength: 9,
  },
  { code: "AF", name: "Afghanistan", dial_code: "+93", phoneLength: 9 },
  {
    code: "AG",
    name: "Antigua and Barbuda",
    dial_code: "+1-268",
    phoneLength: 10,
  },
  { code: "AI", name: "Anguilla", dial_code: "+1-264", phoneLength: 10 },
  { code: "AL", name: "Albania", dial_code: "+355", phoneLength: 9 },
  { code: "AM", name: "Armenia", dial_code: "+374", phoneLength: 6 },
  { code: "AO", name: "Angola", dial_code: "+244", phoneLength: 9 },
  { code: "AQ", name: "Antarctica", dial_code: "+672", phoneLength: 6 },
  { code: "AR", name: "Argentina", dial_code: "+54", phoneLength: [6, 7, 8] },
  { code: "AS", name: "American Samoa", dial_code: "+1-684", phoneLength: 10 },
  { code: "AT", name: "Austria", dial_code: "+43", phoneLength: [10, 11] },
  {
    code: "AU",
    name: "Australia",
    dial_code: "+61",
    suggested: true,
    phoneLength: 9,
  },
  { code: "AW", name: "Aruba", dial_code: "+297", phoneLength: 7 },
  { code: "AX", name: "Alland Islands", dial_code: "+358", min: 7, max: 10 },
  { code: "AZ", name: "Azerbaijan", dial_code: "+994", phoneLength: 9 },
  {
    code: "BA",
    name: "Bosnia and Herzegovina",
    dial_code: "+387",
    phoneLength: 8,
  },
  { code: "BB", name: "Barbados", dial_code: "+1-246", phoneLength: 10 },
  { code: "BD", name: "Bangladesh", dial_code: "+880", phoneLength: 10 },
  { code: "BE", name: "Belgium", dial_code: "+32", phoneLength: 9 },
  { code: "BF", name: "Burkina Faso", dial_code: "+226", phoneLength: 8 },
  { code: "BG", name: "Bulgaria", dial_code: "+359", phoneLength: 9 },
  { code: "BH", name: "Bahrain", dial_code: "+973", phoneLength: 8 },
  { code: "BI", name: "Burundi", dial_code: "+257", phoneLength: 8 },
  { code: "BJ", name: "Benin", dial_code: "+229", phoneLength: 8 },
  { code: "BL", name: "Saint Barthelemy", dial_code: "+590", phoneLength: 9 },
  { code: "BM", name: "Bermuda", dial_code: "+1-441", phoneLength: 10 },
  { code: "BN", name: "Brunei Darussalam", dial_code: "+673", phoneLength: 7 },
  { code: "BO", name: "Bolivia", dial_code: "+591", phoneLength: 9 },
  { code: "BR", name: "Brazil", dial_code: "+55", phoneLength: 11 },
  { code: "BS", name: "Bahamas", dial_code: "+1-242", phoneLength: 10 },
  { code: "BT", name: "Bhutan", dial_code: "+975", phoneLength: 7 },
  { code: "BV", name: "Bouvet Island", dial_code: "+47", phoneLength: 10 },
  { code: "BW", name: "Botswana", dial_code: "+267", phoneLength: 7 },
  { code: "BY", name: "Belarus", dial_code: "+375", phoneLength: 9 },
  { code: "BZ", name: "Belize", dial_code: "+501", phoneLength: 7 },
  {
    code: "CA",
    name: "Canada",
    dial_code: "+1",
    suggested: true,
    phoneLength: 10,
  },
  {
    code: "CC",
    name: "Cocos (Keeling) Islands",
    dial_code: "+61",
    phoneLength: 10,
  },
  {
    code: "CD",
    name: "Congo, Democratic Republic of the",
    dial_code: "+243",
    phoneLength: 7,
  },
  {
    code: "CF",
    name: "Central African Republic",
    dial_code: "+236",
    phoneLength: 8,
  },
  {
    code: "CG",
    name: "Congo, Republic of the",
    dial_code: "+242",
    phoneLength: 9,
  },
  { code: "CH", name: "Switzerland", dial_code: "+41", phoneLength: 9 },
  { code: "CI", name: "Cote d'Ivoire", dial_code: "+225", phoneLength: 8 },
  { code: "CK", name: "Cook Islands", dial_code: "+682", phoneLength: 5 },
  { code: "CL", name: "Chile", dial_code: "+56", phoneLength: 9 },
  { code: "CM", name: "Cameroon", dial_code: "+237", phoneLength: 9 },
  { code: "CN", name: "China", dial_code: "+86", phoneLength: 11 },
  { code: "CO", name: "Colombia", dial_code: "+57", phoneLength: 10 },
  { code: "CR", name: "Costa Rica", dial_code: "+506", phoneLength: 8 },
  { code: "CU", name: "Cuba", dial_code: "+53", phoneLength: 8 },
  { code: "CV", name: "Cape Verde", dial_code: "+238", phoneLength: 7 },
  { code: "CW", name: "Curacao", dial_code: "+599", phoneLength: 7 },
  { code: "CX", name: "Christmas Island", dial_code: "+61", phoneLength: 9 },
  { code: "CY", name: "Cyprus", dial_code: "+357", phoneLength: 8 },
  { code: "CZ", name: "Czech Republic", dial_code: "+420", phoneLength: 9 },
  {
    code: "DE",
    name: "Germany",
    dial_code: "+49",
    suggested: true,
    phoneLength: 10,
  },
  { code: "DJ", name: "Djibouti", dial_code: "+253", phoneLength: 10 },
  { code: "DK", name: "Denmark", dial_code: "+45", phoneLength: 8 },
  { code: "DM", name: "Dominica", dial_code: "+1-767", phoneLength: 10 },
  {
    code: "DO",
    name: "Dominican Republic",
    dial_code: "+1-809",
    phoneLength: 10,
  },
  { code: "DZ", name: "Algeria", dial_code: "+213", phoneLength: 9 },
  { code: "EC", name: "Ecuador", dial_code: "+593", phoneLength: 9 },
  { code: "EE", name: "Estonia", dial_code: "+372", phoneLength: 8 },
  { code: "EG", name: "Egypt", dial_code: "+20", phoneLength: 10 },
  { code: "EH", name: "Western Sahara", dial_code: "+212", phoneLength: 9 },
  { code: "ER", name: "Eritrea", dial_code: "+291", phoneLength: 7 },
  { code: "ES", name: "Spain", dial_code: "+34", phoneLength: 9 },
  { code: "ET", name: "Ethiopia", dial_code: "+251", phoneLength: 9 },
  { code: "FI", name: "Finland", dial_code: "+358", min: 9, max: 11 },
  { code: "FJ", name: "Fiji", dial_code: "+679", phoneLength: 7 },
  {
    code: "FK",
    name: "Falkland Islands (Malvinas)",
    dial_code: "+500",
    phoneLength: 5,
  },
  {
    code: "FM",
    name: "Micronesia, Federated States of",
    dial_code: "+691",
    phoneLength: 7,
  },
  { code: "FO", name: "Faroe Islands", dial_code: "+298", phoneLength: 5 },
  {
    code: "FR",
    name: "France",
    dial_code: "+33",
    suggested: true,
    phoneLength: 9,
  },
  { code: "GA", name: "Gabon", dial_code: "+241", phoneLength: 7 },
  { code: "GB", name: "United Kingdom", dial_code: "+44", phoneLength: 11 },
  { code: "GD", name: "Grenada", dial_code: "+1-473", phoneLength: 10 },
  { code: "GE", name: "Georgia", dial_code: "+995", phoneLength: 9 },
  { code: "GF", name: "French Guiana", dial_code: "+594", phoneLength: 9 },
  { code: "GG", name: "Guernsey", dial_code: "+44", phoneLength: 10 },
  { code: "GH", name: "Ghana", dial_code: "+233", phoneLength: 9 },
  { code: "GI", name: "Gibraltar", dial_code: "+350", phoneLength: 8 },
  { code: "GL", name: "Greenland", dial_code: "+299", phoneLength: 6 },
  { code: "GM", name: "Gambia", dial_code: "+220", phoneLength: 7 },
  { code: "GN", name: "Guinea", dial_code: "+224", phoneLength: 9 },
  { code: "GP", name: "Guadeloupe", dial_code: "+590", phoneLength: 9 },
  { code: "GQ", name: "Equatorial Guinea", dial_code: "+240", phoneLength: 9 },
  { code: "GR", name: "Greece", dial_code: "+30", phoneLength: 10 },
  {
    code: "GS",
    name: "South Georgia and the South Sandwich Islands",
    dial_code: "+500",
    phoneLength: 5,
  },
  { code: "GT", name: "Guatemala", dial_code: "+502", phoneLength: 8 },
  { code: "GU", name: "Guam", dial_code: "+1-671", phoneLength: 10 },
  { code: "GW", name: "Guinea-Bissau", dial_code: "+245", phoneLength: 9 },
  { code: "GY", name: "Guyana", dial_code: "+592", phoneLength: 7 },
  { code: "HK", name: "Hong Kong", dial_code: "+852", phoneLength: 8 },
  {
    code: "HM",
    name: "Heard Island and McDonald Islands",
    dial_code: "+672",
    phoneLength: 10,
  },
  { code: "HN", name: "Honduras", dial_code: "+504", phoneLength: 8 },
  { code: "HR", name: "Croatia", dial_code: "+385", phoneLength: 9 },
  { code: "HT", name: "Haiti", dial_code: "+509", phoneLength: 8 },
  { code: "HU", name: "Hungary", dial_code: "+36", phoneLength: 9 },
  { code: "ID", name: "Indonesia", dial_code: "+62", phoneLength: 11 },
  { code: "IE", name: "Ireland", dial_code: "+353", phoneLength: 9 },
  { code: "IL", name: "Israel", dial_code: "+972", phoneLength: 9 },
  { code: "IM", name: "Isle of Man", dial_code: "+44", phoneLength: 10 },
  { code: "IN", name: "India", dial_code: "+91", phoneLength: 10 },
  {
    code: "IO",
    name: "British Indian Ocean Territory",
    dial_code: "+246",
    phoneLength: 7,
  },
  { code: "IQ", name: "Iraq", dial_code: "+964", phoneLength: 10 },
  {
    code: "IR",
    name: "Iran, Islamic Republic of",
    dial_code: "+98",
    phoneLength: 11,
  },
  { code: "IS", name: "Iceland", dial_code: "+354", phoneLength: 7 },
  { code: "IT", name: "Italy", dial_code: "+39", phoneLength: 10 },
  { code: "JE", name: "Jersey", dial_code: "+44", phoneLength: 10 },
  { code: "JM", name: "Jamaica", dial_code: "+1-876", phoneLength: 10 },
  { code: "JO", name: "Jordan", dial_code: "+962", phoneLength: [8, 9] },
  { code: "JP", name: "Japan", dial_code: "+81", suggested: true },
  { code: "KE", name: "Kenya", dial_code: "+254", phoneLength: 10 },
  { code: "KG", name: "Kyrgyzstan", dial_code: "+996", phoneLength: 9 },
  { code: "KH", name: "Cambodia", dial_code: "+855", phoneLength: 9 },
  { code: "KI", name: "Kiribati", dial_code: "+686", phoneLength: 8 },
  { code: "KM", name: "Comoros", dial_code: "+269", phoneLength: 7 },
  {
    code: "KN",
    name: "Saint Kitts and Nevis",
    dial_code: "+1-869",
    phoneLength: 10,
  },
  {
    code: "KP",
    name: "Korea, Democratic People's Republic of",
    dial_code: "+850",
    phoneLength: [4, 6, 7, 13],
  },
  {
    code: "KR",
    name: "Korea, Republic of",
    dial_code: "+82",
    phoneLength: [7, 8],
  },
  { code: "KW", name: "Kuwait", dial_code: "+965", phoneLength: 8 },
  { code: "KY", name: "Cayman Islands", dial_code: "+1-345", phoneLength: 7 },
  { code: "KZ", name: "Kazakhstan", dial_code: "+7", phoneLength: 10 },
  {
    code: "LA",
    name: "Lao People's Democratic Republic",
    dial_code: "+856",
    phoneLength: [8, 9],
  },
  { code: "LB", name: "Lebanon", dial_code: "+961", phoneLength: [7, 8] },
  { code: "LC", name: "Saint Lucia", dial_code: "+1-758", phoneLength: 7 },
  { code: "LI", name: "Liechtenstein", dial_code: "+423", phoneLength: 7 },
  { code: "LK", name: "Sri Lanka", dial_code: "+94", phoneLength: 7 },
  { code: "LR", name: "Liberia", dial_code: "+231", phoneLength: [8, 9] },
  { code: "LS", name: "Lesotho", dial_code: "+266", phoneLength: 8 },
  { code: "LT", name: "Lithuania", dial_code: "+370", phoneLength: 8 },
  { code: "LU", name: "Luxembourg", dial_code: "+352", phoneLength: 9 },
  { code: "LV", name: "Latvia", dial_code: "+371", phoneLength: 8 },
  { code: "LY", name: "Libya", dial_code: "+218", phoneLength: 10 },
  { code: "MA", name: "Morocco", dial_code: "+212", phoneLength: 9 },
  { code: "MC", name: "Monaco", dial_code: "+377", phoneLength: 8 },
  {
    code: "MD",
    name: "Moldova, Republic of",
    dial_code: "+373",
    phoneLength: 8,
  },
  { code: "ME", name: "Montenegro", dial_code: "+382", phoneLength: 8 },
  {
    code: "MF",
    name: "Saint Martin (French part)",
    dial_code: "+590",
    phoneLength: 6,
  },
  { code: "MG", name: "Madagascar", dial_code: "+261", phoneLength: 7 },
  { code: "MH", name: "Marshall Islands", dial_code: "+692", phoneLength: 7 },
  {
    code: "MK",
    name: "Macedonia, the Former Yugoslav Republic of",
    dial_code: "+389",
    phoneLength: 8,
  },
  { code: "ML", name: "Mali", dial_code: "+223", phoneLength: 8 },
  { code: "MM", name: "Myanmar", dial_code: "+95", min: 7, max: 10 },
  { code: "MN", name: "Mongolia", dial_code: "+976", phoneLength: 8 },
  { code: "MO", name: "Macao", dial_code: "+853", phoneLength: 8 },
  {
    code: "MP",
    name: "Northern Mariana Islands",
    dial_code: "+1-670",
    phoneLength: 7,
  },
  { code: "MQ", name: "Martinique", dial_code: "+596", phoneLength: 9 },
  { code: "MR", name: "Mauritania", dial_code: "+222", phoneLength: 8 },
  { code: "MS", name: "Montserrat", dial_code: "+1-664", phoneLength: 10 },
  { code: "MT", name: "Malta", dial_code: "+356", phoneLength: 8 },
  { code: "MU", name: "Mauritius", dial_code: "+230", phoneLength: 8 },
  { code: "MV", name: "Maldives", dial_code: "+960", phoneLength: 7 },
  { code: "MW", name: "Malawi", dial_code: "+265", phoneLength: [7, 8, 9] },
  { code: "MX", name: "Mexico", dial_code: "+52", phoneLength: 10 },
  { code: "MY", name: "Malaysia", dial_code: "+60", phoneLength: 7 },
  { code: "MZ", name: "Mozambique", dial_code: "+258", phoneLength: 12 },
  { code: "NA", name: "Namibia", dial_code: "+264", phoneLength: 7 },
  { code: "NC", name: "New Caledonia", dial_code: "+687", phoneLength: 6 },
  { code: "NE", name: "Niger", dial_code: "+227", phoneLength: 8 },
  { code: "NF", name: "Norfolk Island", dial_code: "+672", phoneLength: 6 },
  { code: "NG", name: "Nigeria", dial_code: "+234", phoneLength: [7, 11] },
  { code: "NI", name: "Nicaragua", dial_code: "+505", phoneLength: 8 },
  { code: "NL", name: "Netherlands", dial_code: "+31", phoneLength: 9 },
  { code: "NO", name: "Norway", dial_code: "+47", phoneLength: 8 },
  { code: "NP", name: "Nepal", dial_code: "+977", phoneLength: 10 },
  { code: "NR", name: "Nauru", dial_code: "+674", phoneLength: 7 },
  { code: "NU", name: "Niue", dial_code: "+683", phoneLength: 4 },
  { code: "NZ", name: "New Zealand", dial_code: "+64", phoneLength: [8, 9] },
  { code: "OM", name: "Oman", dial_code: "+968", phoneLength: 8 },
  { code: "PA", name: "Panama", dial_code: "+507", phoneLength: 8 },
  { code: "PE", name: "Peru", dial_code: "+51", phoneLength: 9 },
  { code: "PF", name: "French Polynesia", dial_code: "+689", phoneLength: 8 },
  { code: "PG", name: "Papua New Guinea", dial_code: "+675", phoneLength: 8 },
  { code: "PH", name: "Philippines", dial_code: "+63", phoneLength: 10 },
  { code: "PK", name: "Pakistan", dial_code: "+92", phoneLength: 10 },
  { code: "PL", name: "Poland", dial_code: "+48", phoneLength: 9 },
  {
    code: "PM",
    name: "Saint Pierre and Miquelon",
    dial_code: "+508",
    phoneLength: 6,
  },
  { code: "PN", name: "Pitcairn", dial_code: "+870", phoneLength: 9 },
  { code: "PR", name: "Puerto Rico", dial_code: "+1", phoneLength: 10 },
  {
    code: "PS",
    name: "Palestine, State of",
    dial_code: "+970",
    phoneLength: 9,
  },
  { code: "PT", name: "Portugal", dial_code: "+351", phoneLength: 9 },
  { code: "PW", name: "Palau", dial_code: "+680", phoneLength: 7 },
  { code: "PY", name: "Paraguay", dial_code: "+595", phoneLength: 9 },
  { code: "QA", name: "Qatar", dial_code: "+974", phoneLength: 8 },
  { code: "RE", name: "Reunion", dial_code: "+262", phoneLength: 10 },
  { code: "RO", name: "Romania", dial_code: "+40", phoneLength: 10 },
  { code: "RS", name: "Serbia", dial_code: "+381", phoneLength: 9 },
  { code: "RU", name: "Russian Federation", dial_code: "+7", phoneLength: 10 },
  { code: "RW", name: "Rwanda", dial_code: "+250", phoneLength: 9 },
  { code: "SA", name: "Saudi Arabia", dial_code: "+966", phoneLength: 9 },
  { code: "SB", name: "Solomon Islands", dial_code: "+677", phoneLength: 7 },
  { code: "SC", name: "Seychelles", dial_code: "+248", phoneLength: 7 },
  { code: "SD", name: "Sudan", dial_code: "+249", phoneLength: 7 },
  { code: "SE", name: "Sweden", dial_code: "+46", phoneLength: 7 },
  { code: "SG", name: "Singapore", dial_code: "+65", phoneLength: 8 },
  { code: "SH", name: "Saint Helena", dial_code: "+290", phoneLength: 4 },
  { code: "SI", name: "Slovenia", dial_code: "+386", phoneLength: 9 },
  {
    code: "SJ",
    name: "Svalbard and Jan Mayen",
    dial_code: "+47",
    phoneLength: 8,
  },
  { code: "SK", name: "Slovakia", dial_code: "+421", phoneLength: 9 },
  { code: "SL", name: "Sierra Leone", dial_code: "+232", phoneLength: 8 },
  { code: "SM", name: "San Marino", dial_code: "+378", phoneLength: 10 },
  { code: "SN", name: "Senegal", dial_code: "+221", phoneLength: 9 },
  { code: "SO", name: "Somalia", dial_code: "+252", phoneLength: [8, 9] },
  { code: "SR", name: "Suriname", dial_code: "+597", phoneLength: [6, 7] },
  { code: "SS", name: "South Sudan", dial_code: "+211", phoneLength: 7 },
  {
    code: "ST",
    name: "Sao Tome and Principe",
    dial_code: "+239",
    phoneLength: 7,
  },
  { code: "SV", name: "El Salvador", dial_code: "+503", phoneLength: 8 },
  {
    code: "SX",
    name: "Sint Maarten (Dutch part)",
    dial_code: "+1-721",
    phoneLength: 10,
  },
  {
    code: "SY",
    name: "Syrian Arab Republic",
    dial_code: "+963",
    phoneLength: 7,
  },
  { code: "SZ", name: "Swaziland", dial_code: "+268", phoneLength: 8 },
  {
    code: "TC",
    name: "Turks and Caicos Islands",
    dial_code: "+1-649",
    phoneLength: 10,
  },
  { code: "TD", name: "Chad", dial_code: "+235", phoneLength: 6 },
  {
    code: "TF",
    name: "French Southern Territories",
    dial_code: "+262",
    phoneLength: 10,
  },
  { code: "TG", name: "Togo", dial_code: "+228", phoneLength: 8 },
  { code: "TH", name: "Thailand", dial_code: "+66", phoneLength: 9 },
  { code: "TJ", name: "Tajikistan", dial_code: "+992", phoneLength: 9 },
  { code: "TK", name: "Tokelau", dial_code: "+690", phoneLength: 5 },
  { code: "TL", name: "Timor-Leste", dial_code: "+670", phoneLength: 7 },
  { code: "TM", name: "Turkmenistan", dial_code: "+993", phoneLength: 8 },
  { code: "TN", name: "Tunisia", dial_code: "+216", phoneLength: 8 },
  { code: "TO", name: "Tonga", dial_code: "+676", phoneLength: 5 },
  { code: "TR", name: "Turkey", dial_code: "+90", phoneLength: 11 },
  {
    code: "TT",
    name: "Trinidad and Tobago",
    dial_code: "+1-868",
    phoneLength: 7,
  },
  { code: "TV", name: "Tuvalu", dial_code: "+688", phoneLength: 5 },
  {
    code: "TW",
    name: "Taiwan, Province of China",
    dial_code: "+886",
    phoneLength: 9,
  },
  {
    code: "TZ",
    name: "United Republic of Tanzania",
    dial_code: "+255",
    phoneLength: 7,
  },
  { code: "UA", name: "Ukraine", dial_code: "+380", phoneLength: 9 },
  { code: "UG", name: "Uganda", dial_code: "+256", phoneLength: 7 },
  {
    code: "US",
    name: "United States",
    dial_code: "+1",
    suggested: true,
    phoneLength: 10,
  },
  { code: "UY", name: "Uruguay", dial_code: "+598", phoneLength: 8 },
  { code: "UZ", name: "Uzbekistan", dial_code: "+998", phoneLength: 9 },
  {
    code: "VA",
    name: "Holy See (Vatican City State)",
    dial_code: "+379",
    phoneLength: 10,
  },
  {
    code: "VC",
    name: "Saint Vincent and the Grenadines",
    dial_code: "+1-784",
    phoneLength: 7,
  },
  { code: "VE", name: "Venezuela", dial_code: "+58", phoneLength: 7 },
  {
    code: "VG",
    name: "British Virgin Islands",
    dial_code: "+1-284",
    phoneLength: 7,
  },
  {
    code: "VI",
    name: "US Virgin Islands",
    dial_code: "+1-340",
    phoneLength: 10,
  },
  { code: "VN", name: "Vietnam", dial_code: "+84", phoneLength: 9 },
  { code: "VU", name: "Vanuatu", dial_code: "+678", phoneLength: 5 },
  { code: "WF", name: "Wallis and Futuna", dial_code: "+681", phoneLength: 6 },
  { code: "WS", name: "Samoa", dial_code: "+685", phoneLength: [5, 6, 7] },
  { code: "XK", name: "Kosovo", dial_code: "+383", phoneLength: 8 },
  { code: "YE", name: "Yemen", dial_code: "+967", phoneLength: 9 },
  { code: "YT", name: "Mayotte", dial_code: "+262", phoneLength: 9 },
  { code: "ZA", name: "South Africa", dial_code: "+27", phoneLength: 9 },
  { code: "ZM", name: "Zambia", dial_code: "+260", phoneLength: 9 },
  { code: "ZW", name: "Zimbabwe", dial_code: "+263", phoneLength: 9 },
];
export const WALLET_TYPES = [
  {
    name: "Nigerian Naira",
    icon: "emojione:flag-for-nigeria",
    value: "NGN",
    symbol: "₦",
    color: "rgba(48, 168, 94, 1)",
    background: "ngn-bg.png",
  },
  {
    name: "US Dollar",
    icon: "emojione:flag-for-united-states",
    value: "USD",
    symbol: "$",
    color: "rgb(44, 44, 44)",
    background: "usd-bg.png",
  },
  {
    name: "British Pound",
    icon: "emojione:flag-for-united-kingdom",
    value: "GBP",
    color: "rgb(249, 121, 89)",
    symbol: "£",
    background: "gbp-bg.png",
  },
];
export const LGAS = [
  {
    state: "Abia",
    lgas: [
      "Aba North",
      "Aba South",
      "Arochukwu",
      "Bende",
      "Ikawuno",
      "Ikwuano",
      "Isiala-Ngwa North",
      "Isiala-Ngwa South",
      "Isuikwuato",
      "Umu Nneochi",
      "Obi Ngwa",
      "Obioma Ngwa",
      "Ohafia",
      "Ohaozara",
      "Osisioma",
      "Ugwunagbo",
      "Ukwa West",
      "Ukwa East",
      "Umuahia North",
      "Umuahia South",
    ],
  },
  {
    state: "Adamawa",
    lgas: [
      "Demsa",
      "Fufore",
      "Ganye",
      "Girei",
      "Gombi",
      "Guyuk",
      "Hong",
      "Jada",
      "Lamurde",
      "Madagali",
      "Maiha",
      "Mayo-Belwa",
      "Michika",
      "Mubi-North",
      "Mubi-South",
      "Numan",
      "Shelleng",
      "Song",
      "Toungo",
      "Yola North",
      "Yola South",
    ],
  },
  {
    state: "Akwa Ibom",
    lgas: [
      "Abak",
      "Eastern-Obolo",
      "Eket",
      "Esit-Eket",
      "Essien-Udim",
      "Etim-Ekpo",
      "Etinan",
      "Ibeno",
      "Ibesikpo-Asutan",
      "Ibiono-Ibom",
      "Ika",
      "Ikono",
      "Ikot-Abasi",
      "Ikot-Ekpene",
      "Ini",
      "Itu",
      "Mbo",
      "Mkpat-Enin",
      "Nsit-Atai",
      "Nsit-Ibom",
      "Nsit-Ubium",
      "Obot-Akara",
      "Okobo",
      "Onna",
      "Oron",
      "Oruk Anam",
      "Udung-Uko",
      "Ukanafun",
      "Urue-Offong/Oruko",
      "Uruan",
      "Uyo",
    ],
  },
  {
    state: "Anambra",
    lgas: [
      "Aguata",
      "Anambra East",
      "Anambra West",
      "Anaocha",
      "Awka North",
      "Awka South",
      "Ayamelum",
      "Dunukofia",
      "Ekwusigo",
      "Idemili-North",
      "Idemili-South",
      "Ihiala",
      "Njikoka",
      "Nnewi-North",
      "Nnewi-South",
      "Ogbaru",
      "Onitsha-North",
      "Onitsha-South",
      "Orumba-North",
      "Orumba-South",
    ],
  },
  {
    state: "Bauchi",
    lgas: [
      "Alkaleri",
      "Bauchi",
      "Bogoro",
      "Damban",
      "Darazo",
      "Dass",
      "Gamawa",
      "Ganjuwa",
      "Giade",
      "Itas/Gadau",
      "Jama'Are",
      "Katagum",
      "Kirfi",
      "Misau",
      "Ningi",
      "Shira",
      "Tafawa-Balewa",
      "Toro",
      "Warji",
      "Zaki",
    ],
  },
  {
    state: "Benue",
    lgas: [
      "Ado",
      "Agatu",
      "Apa",
      "Buruku",
      "Gboko",
      "Guma",
      "Gwer-East",
      "Gwer-West",
      "Katsina-Ala",
      "Konshisha",
      "Kwande",
      "Logo",
      "Makurdi",
      "Ogbadibo",
      "Ohimini",
      "Oju",
      "Okpokwu",
      "Otukpo",
      "Tarka",
      "Ukum",
      "Ushongo",
      "Vandeikya",
    ],
  },
  {
    state: "Borno",
    lgas: [
      "Abadam",
      "Askira-Uba",
      "Bama",
      "Bayo",
      "Biu",
      "Chibok",
      "Damboa",
      "Dikwa",
      "Gubio",
      "Guzamala",
      "Gwoza",
      "Hawul",
      "Jere",
      "Kaga",
      "Kala/Balge",
      "Konduga",
      "Kukawa",
      "Kwaya-Kusar",
      "Mafa",
      "Magumeri",
      "Maiduguri",
      "Marte",
      "Mobbar",
      "Monguno",
      "Ngala",
      "Nganzai",
      "Shani",
    ],
  },
  {
    state: "Bayelsa",
    lgas: [
      "Brass",
      "Ekeremor",
      "Kolokuma/Opokuma",
      "Nembe",
      "Ogbia",
      "Sagbama",
      "Southern-Ijaw",
      "Yenagoa",
    ],
  },
  {
    state: "Cross River",
    lgas: [
      "Abi",
      "Akamkpa",
      "Akpabuyo",
      "Bakassi",
      "Bekwarra",
      "Biase",
      "Boki",
      "Calabar-Municipal",
      "Calabar-South",
      "Etung",
      "Ikom",
      "Obanliku",
      "Obubra",
      "Obudu",
      "Odukpani",
      "Ogoja",
      "Yakurr",
      "Yala",
    ],
  },
  {
    state: "Delta",
    lgas: [
      "Aniocha North",
      "Aniocha-North",
      "Aniocha-South",
      "Bomadi",
      "Burutu",
      "Ethiope-East",
      "Ethiope-West",
      "Ika-North-East",
      "Ika-South",
      "Isoko-North",
      "Isoko-South",
      "Ndokwa-East",
      "Ndokwa-West",
      "Okpe",
      "Oshimili-North",
      "Oshimili-South",
      "Patani",
      "Sapele",
      "Udu",
      "Ughelli-North",
      "Ughelli-South",
      "Ukwuani",
      "Uvwie",
      "Warri South-West",
      "Warri North",
      "Warri South",
    ],
  },
  {
    state: "Ebonyi",
    lgas: [
      "Abakaliki",
      "Afikpo-North",
      "Afikpo South (Edda)",
      "Ebonyi",
      "Ezza-North",
      "Ezza-South",
      "Ikwo",
      "Ishielu",
      "Ivo",
      "Izzi",
      "Ohaukwu",
      "Onicha",
    ],
  },
  {
    state: "Edo",
    lgas: [
      "Akoko Edo",
      "Egor",
      "Esan-Central",
      "Esan-North-East",
      "Esan-South-East",
      "Esan-West",
      "Etsako-Central",
      "Etsako-East",
      "Etsako-West",
      "Igueben",
      "Ikpoba-Okha",
      "Oredo",
      "Orhionmwon",
      "Ovia-North-East",
      "Ovia-South-West",
      "Owan East",
      "Owan-West",
      "Uhunmwonde",
    ],
  },
  {
    state: "Ekiti",
    lgas: [
      "Ado-Ekiti",
      "Efon",
      "Ekiti-East",
      "Ekiti-South-West",
      "Ekiti-West",
      "Emure",
      "Gbonyin",
      "Ido-Osi",
      "Ijero",
      "Ikere",
      "Ikole",
      "Ilejemeje",
      "Irepodun/Ifelodun",
      "Ise-Orun",
      "Moba",
      "Oye",
    ],
  },
  {
    state: "Enugu",
    lgas: [
      "Aninri",
      "Awgu",
      "Enugu-East",
      "Enugu-North",
      "Enugu-South",
      "Ezeagu",
      "Igbo-Etiti",
      "Igbo-Eze-North",
      "Igbo-Eze-South",
      "Isi-Uzo",
      "Nkanu-East",
      "Nkanu-West",
      "Nsukka",
      "Oji-River",
      "Udenu",
      "Udi",
      "Uzo-Uwani",
    ],
  },
  {
    state: "Federal Capital Territory",
    lgas: ["Abuja", "Kwali", "Kuje", "Gwagwalada", "Bwari", "Abaji"],
  },
  {
    state: "Gombe",
    lgas: [
      "Akko",
      "Balanga",
      "Billiri",
      "Dukku",
      "Funakaye",
      "Gombe",
      "Kaltungo",
      "Kwami",
      "Nafada",
      "Shongom",
      "Yamaltu/Deba",
    ],
  },
  {
    state: "Imo",
    lgas: [
      "Aboh-Mbaise",
      "Ahiazu-Mbaise",
      "Ehime-Mbano",
      "Ezinihitte",
      "Ideato-North",
      "Ideato-South",
      "Ihitte/Uboma",
      "Ikeduru",
      "Isiala-Mbano",
      "Isu",
      "Mbaitoli",
      "Ngor-Okpala",
      "Njaba",
      "Nkwerre",
      "Nwangele",
      "Obowo",
      "Oguta",
      "Ohaji-Egbema",
      "Okigwe",
      "Onuimo",
      "Orlu",
      "Orsu",
      "Oru-East",
      "Oru-West",
      "Owerri-Municipal",
      "Owerri-North",
      "Owerri-West",
    ],
  },
  {
    state: "Jigawa",
    lgas: [
      "Auyo",
      "Babura",
      "Biriniwa",
      "Birnin-Kudu",
      "Buji",
      "Dutse",
      "Gagarawa",
      "Garki",
      "Gumel",
      "Guri",
      "Gwaram",
      "Gwiwa",
      "Hadejia",
      "Jahun",
      "Kafin-Hausa",
      "Kaugama",
      "Kazaure",
      "Kiri kasama",
      "Maigatari",
      "Malam Madori",
      "Miga",
      "Ringim",
      "Roni",
      "Sule-Tankarkar",
      "Taura",
      "Yankwashi",
    ],
  },
  {
    state: "Kebbi",
    lgas: [
      "Aleiro",
      "Arewa-Dandi",
      "Argungu",
      "Augie",
      "Bagudo",
      "Birnin-Kebbi",
      "Bunza",
      "Dandi",
      "Fakai",
      "Gwandu",
      "Jega",
      "Kalgo",
      "Koko-Besse",
      "Maiyama",
      "Ngaski",
      "Sakaba",
      "Shanga",
      "Suru",
      "Wasagu/Danko",
      "Yauri",
      "Zuru",
    ],
  },
  {
    state: "Kaduna",
    lgas: [
      "Birnin-Gwari",
      "Chikun",
      "Giwa",
      "Igabi",
      "Ikara",
      "Jaba",
      "Jema'A",
      "Kachia",
      "Kaduna-North",
      "Kaduna-South",
      "Kagarko",
      "Kajuru",
      "Kaura",
      "Kauru",
      "Kubau",
      "Kudan",
      "Lere",
      "Makarfi",
      "Sabon-Gari",
      "Sanga",
      "Soba",
      "Zangon-Kataf",
      "Zaria",
    ],
  },
  {
    state: "Kano",
    lgas: [
      "Ajingi",
      "Albasu",
      "Bagwai",
      "Bebeji",
      "Bichi",
      "Bunkure",
      "Dala",
      "Dambatta",
      "Dawakin-Kudu",
      "Dawakin-Tofa",
      "Doguwa",
      "Fagge",
      "Gabasawa",
      "Garko",
      "Garun-Mallam",
      "Gaya",
      "Gezawa",
      "Gwale",
      "Gwarzo",
      "Kabo",
      "Kano-Municipal",
      "Karaye",
      "Kibiya",
      "Kiru",
      "Kumbotso",
      "Kunchi",
      "Kura",
      "Madobi",
      "Makoda",
      "Minjibir",
      "Nasarawa",
      "Rano",
      "Rimin-Gado",
      "Rogo",
      "Shanono",
      "Sumaila",
      "Takai",
      "Tarauni",
      "Tofa",
      "Tsanyawa",
      "Tudun-Wada",
      "Ungogo",
      "Warawa",
      "Wudil",
    ],
  },
  {
    state: "Kogi",
    lgas: [
      "Adavi",
      "Ajaokuta",
      "Ankpa",
      "Dekina",
      "Ibaji",
      "Idah",
      "Igalamela-Odolu",
      "Ijumu",
      "Kabba/Bunu",
      "Kogi",
      "Lokoja",
      "Mopa-Muro",
      "Ofu",
      "Ogori/Magongo",
      "Okehi",
      "Okene",
      "Olamaboro",
      "Omala",
      "Oyi",
      "Yagba-East",
      "Yagba-West",
    ],
  },
  {
    state: "Katsina",
    lgas: [
      "Bakori",
      "Batagarawa",
      "Batsari",
      "Baure",
      "Bindawa",
      "Charanchi",
      "Dan-Musa",
      "Dandume",
      "Danja",
      "Daura",
      "Dutsi",
      "Dutsin-Ma",
      "Faskari",
      "Funtua",
      "Ingawa",
      "Jibia",
      "Kafur",
      "Kaita",
      "Kankara",
      "Kankia",
      "Katsina",
      "Kurfi",
      "Kusada",
      "Mai-Adua",
      "Malumfashi",
      "Mani",
      "Mashi",
      "Matazu",
      "Musawa",
      "Rimi",
      "Sabuwa",
      "Safana",
      "Sandamu",
      "Zango",
    ],
  },
  {
    state: "Kwara",
    lgas: [
      "Asa",
      "Baruten",
      "Edu",
      "Ekiti (Araromi/Opin)",
      "Ilorin-East",
      "Ilorin-South",
      "Ilorin-West",
      "Isin",
      "Kaiama",
      "Moro",
      "Offa",
      "Oke-Ero",
      "Oyun",
      "Pategi",
    ],
  },
  {
    state: "Lagos",
    lgas: [
      "Agege",
      "Ajeromi-Ifelodun",
      "Alimosho",
      "Amuwo-Odofin",
      "Apapa",
      "Badagry",
      "Epe",
      "Eti-Osa",
      "Ibeju-Lekki",
      "Ifako-Ijaiye",
      "Ikeja",
      "Ikorodu",
      "Kosofe",
      "Lagos-Island",
      "Lagos-Mainland",
      "Mushin",
      "Ojo",
      "Oshodi-Isolo",
      "Shomolu",
      "Surulere",
      "Yewa-South",
    ],
  },
  {
    state: "Nasarawa",
    lgas: [
      "Akwanga",
      "Awe",
      "Doma",
      "Karu",
      "Keana",
      "Keffi",
      "Kokona",
      "Lafia",
      "Nasarawa",
      "Nasarawa-Eggon",
      "Obi",
      "Wamba",
      "Toto",
    ],
  },
  {
    state: "Niger",
    lgas: [
      "Agaie",
      "Agwara",
      "Bida",
      "Borgu",
      "Bosso",
      "Chanchaga",
      "Edati",
      "Gbako",
      "Gurara",
      "Katcha",
      "Kontagora",
      "Lapai",
      "Lavun",
      "Magama",
      "Mariga",
      "Mashegu",
      "Mokwa",
      "Moya",
      "Paikoro",
      "Rafi",
      "Rijau",
      "Shiroro",
      "Suleja",
      "Tafa",
      "Wushishi",
    ],
  },
  {
    state: "Ogun",
    lgas: [
      "Abeokuta-North",
      "Abeokuta-South",
      "Ado-Odo/Ota",
      "Ewekoro",
      "Ifo",
      "Ijebu-East",
      "Ijebu-North",
      "Ijebu-North-East",
      "Ijebu-Ode",
      "Ikenne",
      "Imeko-Afon",
      "Ipokia",
      "Obafemi-Owode",
      "Odeda",
      "Odogbolu",
      "Ogun-Waterside",
      "Remo-North",
      "Shagamu",
      "Yewa North",
    ],
  },
  {
    state: "Ondo",
    lgas: [
      "Akoko North-East",
      "Akoko North-West",
      "Akoko South-West",
      "Akoko South-East",
      "Akure-North",
      "Akure-South",
      "Ese-Odo",
      "Idanre",
      "Ifedore",
      "Ilaje",
      "Ile-Oluji-Okeigbo",
      "Irele",
      "Odigbo",
      "Okitipupa",
      "Ondo West",
      "Ondo-East",
      "Ose",
      "Owo",
    ],
  },
  {
    state: "Osun",
    lgas: [
      "Atakumosa West",
      "Atakumosa East",
      "Ayedaade",
      "Ayedire",
      "Boluwaduro",
      "Boripe",
      "Ede South",
      "Ede North",
      "Egbedore",
      "Ejigbo",
      "Ife North",
      "Ife South",
      "Ife-Central",
      "Ife-East",
      "Ifelodun",
      "Ila",
      "Ilesa-East",
      "Ilesa-West",
      "Irepodun",
      "Irewole",
      "Isokan",
      "Iwo",
      "Obokun",
      "Odo-Otin",
      "Ola Oluwa",
      "Olorunda",
      "Oriade",
      "Orolu",
      "Osogbo",
    ],
  },
  {
    state: "Oyo",
    lgas: [
      "Afijio",
      "Akinyele",
      "Atiba",
      "Atisbo",
      "Egbeda",
      "Ibadan North",
      "Ibadan North-East",
      "Ibadan North-West",
      "Ibadan South-East",
      "Ibadan South-West",
      "Ibarapa-Central",
      "Ibarapa-East",
      "Ibarapa-North",
      "Ido",
      "Ifedayo",
      "Irepo",
      "Iseyin",
      "Itesiwaju",
      "Iwajowa",
      "Kajola",
      "Lagelu",
      "Ogo-Oluwa",
      "Ogbomosho-North",
      "Ogbomosho-South",
      "Olorunsogo",
      "Oluyole",
      "Ona-Ara",
      "Orelope",
      "Ori-Ire",
      "Oyo-West",
      "Oyo-East",
      "Saki-East",
      "Saki-West",
      "Surulere",
    ],
  },
  {
    state: "Plateau",
    lgas: [
      "Barkin-Ladi",
      "Bassa",
      "Bokkos",
      "Jos-East",
      "Jos-North",
      "Jos-South",
      "Kanam",
      "Kanke",
      "Langtang-North",
      "Langtang-South",
      "Mangu",
      "Mikang",
      "Pankshin",
      "Qua'an Pan",
      "Riyom",
      "Shendam",
      "Wase",
    ],
  },
  {
    state: "Rivers",
    lgas: [
      "Abua/Odual",
      "Ahoada-East",
      "Ahoada-West",
      "Akuku Toru",
      "Andoni",
      "Asari-Toru",
      "Bonny",
      "Degema",
      "Eleme",
      "Emuoha",
      "Etche",
      "Gokana",
      "Ikwerre",
      "Khana",
      "Obio/Akpor",
      "Ogba-Egbema-Ndoni",
      "Ogba/Egbema/Ndoni",
      "Ogu/Bolo",
      "Okrika",
      "Omuma",
      "Opobo/Nkoro",
      "Oyigbo",
      "Port-Harcourt",
      "Tai",
    ],
  },
  {
    state: "Sokoto",
    lgas: [
      "Binji",
      "Bodinga",
      "Dange-Shuni",
      "Gada",
      "Goronyo",
      "Gudu",
      "Gwadabawa",
      "Illela",
      "Kebbe",
      "Kware",
      "Rabah",
      "Sabon Birni",
      "Shagari",
      "Silame",
      "Sokoto-North",
      "Sokoto-South",
      "Tambuwal",
      "Tangaza",
      "Tureta",
      "Wamako",
      "Wurno",
      "Yabo",
    ],
  },
  {
    state: "Taraba",
    lgas: [
      "Ardo-Kola",
      "Bali",
      "Donga",
      "Gashaka",
      "Gassol",
      "Ibi",
      "Jalingo",
      "Karim-Lamido",
      "Kurmi",
      "Lau",
      "Sardauna",
      "Takum",
      "Ussa",
      "Wukari",
      "Yorro",
      "Zing",
    ],
  },
  {
    state: "Yobe",
    lgas: [
      "Bade",
      "Bursari",
      "Damaturu",
      "Fika",
      "Fune",
      "Geidam",
      "Gujba",
      "Gulani",
      "Jakusko",
      "Karasuwa",
      "Machina",
      "Nangere",
      "Nguru",
      "Potiskum",
      "Tarmuwa",
      "Yunusari",
      "Yusufari",
    ],
  },
  {
    state: "Zamfara",
    lgas: [
      "Anka",
      "Bakura",
      "Birnin Magaji/Kiyaw",
      "Bukkuyum",
      "Bungudu",
      "Gummi",
      "Gusau",
      "Isa",
      "Kaura-Namoda",
      "Kiyawa",
      "Maradun",
      "Maru",
      "Shinkafi",
      "Talata-Mafara",
      "Tsafe",
      "Zurmi",
    ],
  },
];
export const DEVICE_STATUS = [
  { label: "Logged out", color: "error" },
  {
    label: "Logged in",
    color: "success",
  },
];
export const REVIEW_STATUS = [
  { label: "Pending", color: "warning" },
  {
    label: "Approved",
    color: "success",
  },
  {
    label: "Rejected",
    color: "error",
  },
];
export const DEVICE_TYPES = [
  { label: "Web", icon: "streamline:web", color: "error" },
  {
    label: "Windows",
    icon: "mingcute:windows-fill",
    color: "success",
  },
  {
    label: "macOs",
    icon: "wpf:macos",
    color: "success",
  },
  {
    label: "Android",
    icon: "ic:round-phone-android",
    color: "success",
  },
  {
    label: "iOs",
    icon: "material-symbols:phone-iphone-outline",
    color: "success",
  },
];
export const PAYMENT_METHODS = [
  "Cash",
  "Bank Transfer",
  "POS",
  "Complimentary",
  "Other",
];
export const BOOKING_PAYMENT_METHODS = [
  "Cash",
  "Card / Transfer",
  "Complimentary",
  "Other",
];
export const TERMINAL_OPTIONS = [
  {
    image: "physical-terminal.png",
    name: "Physical Terminal",
    features: [
      "4inch 480 x 800 pixels capacitive touch screen",
      "Built-in receipt printer",
      "Paper and email receipts",
      "Suited for business with multiple staff accepting frequent payments",
    ],
    benefits: [
      "Receive payments in your Bank account the next business day",
      "No monthly fees",
      "Free 4G SIM with lifetime data connectivity",
      "Assign team members to take card payments",
    ],
  },
  {
    image: "virtual-terminal.svg",
    name: "Virtual Terminal",
    features: [
      "Fast bank transfer confirmations, at scale",
      "Deploy your point-of-sale solution in seconds",
      "No extra hardware needed",
      "Suited for business with multiple staff accepting frequent payments",
    ],
    benefits: [
      "Receive payments in your Bank account the next business day",
      "No monthly fees",
    ],
  },
];
export const PAYMENT_METHODS_LIST = [
  { label: "mastercard", value: "mastercard", icon: <MasterCardIcon /> },
  { label: "visa", value: "visa", icon: <VisaIcon /> },
  { label: "Paypal", value: "paypal", icon: "paypal.svg" },
  { label: "Amex", value: "amex", icon: "amex.svg" },
  { label: "Discover", value: "discover", icon: "discover.svg" },
  { label: "Maestro", value: "maestro", icon: "maestro.svg" },
  { label: "Stripe", value: "stripe", icon: "stripe.svg" },
  { label: "Bank Transfer", value: "transfer", icon: "" },
  { label: "Cash", value: "cash", icon: "" },
];
export const LISTING_STATUS = [
  { label: "Listing in progress", color: "warning", value: "0" },
  {
    label: "Completed",
    color: "success",
    value: "1",
  },
  {
    label: "Verification required",
    color: "info",
    value: "2",
  },
];
export const PUBLISH_STATUS = [
  { label: "Hidden", color: "warning", value: "0" },
  {
    label: "Displayed",
    color: "success",
    value: "1",
  },
  {
    label: "Verification required",
    color: "info",
    value: "2",
  },
];
export const STAFF_STATUS = [
  { label: "Suspended", color: "warning", value: "0" },
  {
    label: "Active",
    color: "success",
    value: "1",
  },
];
export const REFERRAL_STATUS = [
  { label: "Pending", color: "warning", value: "0" },
  {
    label: "Active",
    color: "success",
    value: "1",
  },
];
export const PROPERTY_STATUS = [
  { label: "Listing in progress", color: "warning", value: "0" },
  {
    label: "Active",
    color: "success",
    value: "1",
  },
  {
    label: "Rented",
    color: "info",
    value: "2",
  },
  {
    label: "Expired",
    color: "error",
    value: "4",
  },
];
export const PAYMENT_STATUS = [
  { label: "Pending", color: "warning", value: "0" },
  {
    label: "Paid",
    color: "success",
    value: "1",
  },
];
export const PAYMENT_TYPE = [
  { label: "Bank Transfer", color: "info", value: "0" },
  {
    label: "POS",
    color: "success",
    value: "1",
  },
];
export const BOOKING_STATUS = [
  { label: "Upcoming", color: "warning", value: "0" },
  {
    label: "Confirmed",
    color: "success",
    value: "1",
  },
  {
    label: "Cancelled",
    color: "error",
    value: "2",
  },
  // {
  //   label: "Deleted",
  //   color: "error",
  //   value: "3",
  // },
  {
    label: "Completed",
    color: "info",
    value: "4",
  },
];
export const getBookingStatus = (status) =>
  BOOKING_STATUS.find((item) => String(item.value) === String(status)) || {};
export const LEAD_STATUS = [
  { label: "New", color: "info", value: "0" },
  {
    label: "Contacted",
    color: "success",
    value: "1",
  },
  {
    label: "Inprogress",
    color: "error",
    value: "2",
  },
];
export const LEAD_PRIORITY = [
  { label: "Low", color: "success", value: "0" },
  {
    label: "High",
    color: "success",
    value: "1",
  },
  {
    label: "Medium",
    color: "success",
    value: "2",
  },
];
export const BOOKING_TYPE = [
  { label: "Afritas Booking", color: "info", value: "0" },
  {
    label: "Host booking",
    color: "success",
    value: "1",
  },
];
export const COMPLIANCE_TYPE = [
  { label: "NIN", value: "nin", title: "Enter your NIN:", type: "individual" },
  {
    label: "Driver's license",
    value: "driver",
    title: "Enter your Driver's license Number:",
    type: "individual",
  },
  {
    label: "International Passport",
    value: "passport",
    title: "Enter your Passport ID:",
    type: "individual",
  },
  {
    label: "CAC",
    value: "cac",
    title: "Enter your Business RC / BN Number:",
    type: "business",
  },
];
export const COMPLIANCE_STATUS = [
  { label: "Pending", color: "warning" },
  {
    label: "Verified",
    color: "success",
  },
  { label: "Failed", color: "error" },
];
export const SUPPORT_STATUS = [
  {
    label: "Pending",
    color: "warning",
    value: "0",
    icon: "material-symbols:pending-actions",
  },
  {
    label: "Replied",
    color: "success",
    value: "1",
    icon: "icon-park-solid:message-sent",
  },
];
export const SUPPORT_REASONS = [
  "Enquiries",
  "Payment Not Made",
  "System Error",
  "Others",
];
export const LOG_TYPES = [
  { label: "System", value: "0", color: "primary" },
  { label: "Administrator", value: "1", color: "secondary" },
  { label: "Role", value: "2", color: "secondary" },
  { label: "Staff", value: "3", color: "secondary" },
  { label: "Branch", value: "4", color: "info" },
  { label: "Product Category", value: "5", color: "info" },
  { label: "Customer", value: "6", color: "info" },
  { label: "Product", value: "7", color: "info" },
  { label: "Sale/Invoice", value: "8", color: "info" },
  { label: "Returned Product", value: "9", color: "info" },
  { label: "Debtor", value: "10", color: "info" },
  { label: "Damaged Product", value: "11", color: "info" },
  { label: "Purchases", value: "12", color: "info" },
  { label: "Online Sales", value: "13", color: "info" },
  { label: "Gift Card", value: "14", color: "info" },
  { label: "Loyalty Card", value: "15", color: "info" },
  { label: "Wallet", value: "16", color: "warning" },
  { label: "Withdrawal", value: "17", color: "warning" },
  { label: "Expenses", value: "18", color: "warning" },
  { label: "Subscription", value: "19", color: "warning" },
  { label: "Message", value: "20", color: "success" },
  { label: "Import/Export", value: "21", color: "success" },
  { label: "Login", value: "22", color: "success" },
];
export const PROPERTY_TYPES = [
  { label: "Hotel", value: "hotel", icon: "material-symbols:bed-outline" },
  {
    label: "Camping house",
    value: "camping_house",
    icon: "fluent-emoji-high-contrast:camping",
  },
  {
    label: "Flat/Apartment",
    value: "apartment",
    icon: "material-symbols-light:apartment",
  },
  { label: "Boathouse", value: "boathouse", icon: "map:boat-tour" },
  { label: "Penthouse", value: "penthouse", icon: "ph:building" },
  { label: "Villa", value: "villa", icon: "material-symbols:villa-outline" },
  {
    label: "Bed & breakfast",
    value: "bedbreakfast",
    icon: "fluent-mdl2:breakfast",
  },
  {
    label: "Guest house",
    value: "guesthouse",
    icon: "hugeicons:guest-house",
  },
  { label: "Cabin", value: "cabin", icon: "material-symbols:cabin" },
  {
    label: "Bungalow",
    value: "bungalow",
    icon: "material-symbols:bungalow-outline",
  },
  { label: "House", value: "house", icon: "mingcute:house-2-line" },
  { label: "Farm house", value: "farmhouse", icon: "mdi:farm-home-outline" },
  {
    label: "Chalet",
    value: "chalet",
    icon: "material-symbols:chalet-outline",
  },
  {
    label: "Cottage",
    value: "cottage",
    icon: "material-symbols:cottage-outline",
  },
  {
    label: "Townhouse",
    value: "townhouse",
    icon: "fluent:building-townhouse-32-regular",
  },
  { label: "Resort", value: "resort", icon: "fluent-mdl2:ski-resorts" },
];
export const AUTOMOBILE_TYPES = [
  { label: "Saloon", value: "saloon", icon: "mdi:car-saloon" },
  {
    label: "Hatchback",
    value: "hatchback",
    icon: "mdi:car-hatchback",
  },
  {
    label: "Estate",
    value: "estate",
    icon: "mdi:car-estate",
  },
  { label: "4x4", value: "4x4", icon: "healthicons:4x4-outline" },
  {
    label: "MPV",
    value: "mpv",
    icon: "fluent:vehicle-car-profile-ltr-20-regular",
  },
  {
    label: "Convertible",
    value: "convertible",
    icon: "mdi:car-convertible",
  },
  {
    label: "Coupe",
    value: "coupe",
    icon: "hugeicons:car-03",
  },
  {
    label: "City-car",
    value: "citycar",
    icon: "game-icons:city-car",
  },
  {
    label: "Van or bus",
    value: "van",
    icon: "ph:van",
  },
  {
    label: "Off-road",
    value: "offroad",
    icon: "mdi:truck-off-road",
  },
];
export const BOAT_TYPES = [
  { label: "Yacht", value: "yacht", icon: "fontisto:yacht" },
  {
    label: "Sailboat",
    value: "sailboat",
    icon: "game-icons:shooner-sailboat",
  },
  {
    label: "Catamaran",
    value: "catamaran",
    icon: "arcticons:boat-crest",
  },
  {
    label: "Motorboat",
    value: "motorboat",
    icon: "emojione-monotone:motor-boat",
  },
  {
    label: "House boat",
    value: "houseboat",
    icon: "material-symbols:houseboat-outline",
  },
  {
    label: "Ship",
    value: "ship",
    icon: "mingcute:ship-line",
  },
];
export const LISTING_TYPES = [
  { label: "House", value: "house", icon: "mingcute:house-2-line" },
  {
    label: "Flat/Apartment",
    value: "apartment",
    icon: "material-symbols-light:apartment",
  },
  { label: "Boathouse", value: "boathouse", icon: "map:boat-tour" },
  { label: "Penthouse", value: "penthouse", icon: "ph:building" },
  { label: "Villa", value: "villa", icon: "material-symbols:villa-outline" },
  {
    label: "Townhouse",
    value: "townhouse",
    icon: "fluent:building-townhouse-32-regular",
  },
  {
    label: "Guest house",
    value: "guesthouse",
    icon: "hugeicons:guest-house",
  },
  { label: "Cabin", value: "cabin", icon: "material-symbols:cabin" },
  {
    label: "Bungalow",
    value: "bungalow",
    icon: "material-symbols:bungalow-outline",
  },
  { label: "Condo", value: "condo", icon: "bi:buildings" },
  { label: "Farm house", value: "farmhouse", icon: "mdi:farm-home-outline" },
  {
    label: "Chalet",
    value: "chalet",
    icon: "material-symbols:chalet-outline",
  },
  {
    label: "Cottage",
    value: "cottage",
    icon: "material-symbols:cottage-outline",
  },

  { label: "Resort", value: "resort", icon: "fluent-mdl2:ski-resorts" },
];
export const EVENTS_CATEGORIES = [
  {
    name: "Music",
    value: "music",
    subcategories: [
      "Concerts",
      "Festivals",
      "Live Bands",
      "DJ Events",
      "Open Mic Nights",
    ],
  },
  {
    name: "Sports",
    value: "sports",
    subcategories: [
      "Matches and Games (e.g., Football, Basketball)",
      "Marathons and Races",
      "Fitness Classes",
      "Yoga Sessions",
      "Extreme Sports",
    ],
  },
  {
    name: "Arts and Culture",
    value: "arts-culture",
    subcategories: [
      "Art Exhibitions",
      "Theatre and Plays",
      "Museums and Galleries",
      "Cultural Festivals",
      "Dance Performances",
    ],
  },
  {
    name: "Food and Drink",
    value: "food-drink",
    subcategories: [
      "Food Festivals",
      "Wine Tastings",
      "Cooking Classes",
      "Brewery Tours",
      "Food Truck Rallies",
    ],
  },
  {
    name: "Technology",
    value: "technology",
    subcategories: [
      "Tech Conferences",
      "Hackathons",
      "Startup Pitches",
      "Coding Bootcamps",
      "Product Launches",
    ],
  },
  {
    name: "Education",
    value: "education",
    subcategories: [
      "Workshops",
      "Seminars",
      "Webinars",
      "Lectures",
      "Training Programs",
    ],
  },
  {
    name: "Networking",
    value: "networking",
    subcategories: [
      "Business Meetups",
      "Industry Conferences",
      "Career Fairs",
      "Professional Development",
      "Alumni Events",
    ],
  },
  {
    name: "Family and Kids",
    value: "family-kids",
    subcategories: [
      "Family Festivals",
      "Kids' Workshops",
      "Storytelling Sessions",
      "Amusement Park Events",
      "Family Movie Nights",
    ],
  },
  {
    name: "Health and Wellness",
    value: "health-wellness",
    subcategories: [
      "Health Fairs",
      "Meditation Sessions",
      "Fitness Expos",
      "Wellness Retreats",
      "Nutritional Workshops",
    ],
  },
  {
    name: "Film and Media",
    value: "film-media",
    subcategories: [
      "Film Screenings",
      "Film Festivals",
      "Media Conferences",
      "Photography Exhibitions",
      "Book Readings",
    ],
  },
  {
    name: "Charity and Causes",
    value: "charity-causes",
    subcategories: [
      "Fundraisers",
      "Charity Walks/Runs",
      "Benefit Concerts",
      "Volunteer Opportunities",
      "Awareness Campaigns",
    ],
  },
  {
    name: "Hobbies and Interests",
    value: "hobbies-interests",
    subcategories: [
      "Craft Fairs",
      "Hobby Clubs (e.g., knitting, model building)",
      "Gaming Tournaments",
      "Book Clubs",
      "Gardening Workshops",
    ],
  },
  {
    name: "Fashion",
    value: "fashion",
    subcategories: [
      "Fashion Shows",
      "Designer Showcases",
      "Clothing Swap Meets",
      "Beauty Workshops",
      "Fashion Expos",
    ],
  },
  {
    name: "Travel and Outdoor",
    value: "travel-outdoor",
    subcategories: [
      "Adventure Trips",
      "Hiking Expeditions",
      "Camping Events",
      "City Tours",
      "Wildlife Safaris",
    ],
  },
  {
    name: "Religion and Spirituality",
    value: "religion-spirituality",
    subcategories: [
      "Spiritual Retreats",
      "Religious Festivals",
      "Faith-Based Conferences",
      "Prayer Meetings",
      "Meditation Workshops",
    ],
  },
  {
    name: "Government and Politics",
    value: "government-politics",
    subcategories: [
      "Political Rallies",
      "Town Hall Meetings",
      "Public Debates",
      "Policy Workshops",
      "Advocacy Events",
    ],
  },
  {
    name: "Science and Nature",
    value: "science-nature",
    subcategories: [
      "Science Fairs",
      "Nature Walks",
      "Astronomy Nights",
      "Environmental Workshops",
      "Botanical Garden Tours",
    ],
  },
  {
    name: "Shopping and Markets",
    value: "shopping-markets",
    subcategories: [
      "Flea Markets",
      "Farmers' Markets",
      "Craft Markets",
      "Pop-Up Shops",
      "Antique Fairs",
    ],
  },
  {
    name: "Holiday and Seasonal",
    value: "holiday-seasonal",
    subcategories: [
      "Christmas Markets",
      "Halloween Parties",
      "New Year's Eve Events",
      "Summer Festivals",
      "Easter Egg Hunts",
    ],
  },
  {
    name: "Virtual Events",
    value: "virtual-events",
    subcategories: [
      "Online Conferences",
      "Virtual Workshops",
      "Webinars",
      "Virtual Meetups",
      "Online Concerts",
    ],
  },
];
export const ATTRACTION_TICKETS_CATEGORIES = [
  {
    label: "All ages",
    value: "all",
  },
  {
    label: "Adults (age 12 - 99)",
    value: "adults",
  },
  {
    label: "Children (age 4 - 11)",
    value: "children",
  },
  {
    label: "Infants (age 0 - 3)",
    value: "infants",
  },
];
export const ATTRACTION_CATEGORIES = [
  {
    name: "Museum",
    value: "museum",
    subcategories: [],
  },
  {
    name: "Park",
    value: "park",
    subcategories: [],
  },
  {
    name: "Historical Site",
    value: "historical_site",
    subcategories: [],
  },
  {
    name: "Amusement Park",
    value: "amusement_park",
    subcategories: [],
  },
  {
    name: "Arts and Culture",
    value: "arts-culture",
    subcategories: [],
  },
  {
    name: "Food and drink",
    value: "food-drink",
    subcategories: [],
  },
  {
    name: "Fashion",
    value: "fashion",
    subcategories: [],
  },
  {
    name: "Travel & Outdoors",
    value: "travel",
    subcategories: [],
  },
  {
    name: "Science & Nature",
    value: "science-nature",
    subcategories: [],
  },
  {
    name: "Holiday & Seasonal",
    value: "holiday-seasonal",
    subcategories: [],
  },
  {
    name: "Tour",
    value: "tour",
    subcategories: [],
  },
  {
    name: "Music & Entertainment",
    value: "music-entertainment",
    subcategories: [],
  },
];
export const AUTOMOBILE_MODELS = [
  "Audi",
  "BMW",
  "Ford",
  "Jaguar",
  "Land Rover",
  "Mercedes-Benz",
  "Nissan",
  "Toyota",
  "Vauxhall",
  "Volkswagen",
  "Volvo",
  "Abarth",
  "Aixam",
  "Alfa Romeo",
  "Alpine",
  "Aston Martin",
  "Austin",
  "Bentley",
  "BMW",
  "BYD",
  "Cadillac",
  "Caterham",
  "Chevrolet",
  "Chrysler",
  "Citroen",
  "Coleman Milne",
  "Corvette",
  "Cupra",
  "Dacia",
  "Daewoo",
  "Daihatsu",
  "Dodge",
  "DS",
  "Eagle",
  "Ferrari",
  "Fiat",
  "Fisker",
  "Genesis",
  "Gwm Ora",
  "Honda",
  "Hummer",
  "Hyundai",
  "Ineos",
  "Infiniti",
  "Jaguar",
  "Jeep",
  "KGM",
  "KIA",
  "Lamborghini",
  "Land Rover",
  "Levc",
  "Lexus",
  "Lotus",
  "Marlin",
  "Maserati",
  "Mazda",
];
export const BOAT_MODELS = [
  {
    category: "Sailboats:",
    types: ["Catamaran", "Schooner", "Ketch", "Cutter"],
  },
  {
    category: "Motorboats:",
    types: ["Speedboat", "Cabin Cruiser", "Bowrider", "Cuddy Cabin", "Yacht"],
  },
  {
    category: "Fishing Boats:",
    types: [
      "Bass Boat",
      "Center Console",
      "Walkaround",
      "Sportfishing Yacht",
      "Pontoon Boat",
    ],
  },
  {
    category: "Personal Watercraft:",
    types: ["Jet Ski", "WaveRunner", "Sea-Doo"],
  },
  {
    category: "Luxury Boats:",
    types: ["Superyacht", "Mega Yacht", "Luxury Catamaran", "Motor Yacht"],
  },
  {
    category: "Houseboats:",
    types: [
      "Floating Home",
      "Luxury Houseboat",
      "River Houseboat",
      "Canal Boat",
    ],
  },
];
export const AUTOMOBILE_FUEL_TYPE = [
  "Hybrid",
  "Full Electric",
  "Diesel",
  "Gasoline",
];
export const BOAT_FUEL_TYPE = ["Automatic", "Diesel", "Gasoline"];
export const BOAT_IDEAL = [
  "Private parties",
  "Fishing trips",
  "Water skiing",
  "Weekend gateaways",
];
export const AUTOMOBILE_TRANSMISSIONS = ["Manual", "Automatic"];
export const BOAT_ENGINE_TYPE = [
  "Inboard",
  "Outboard",
  "Sail",
  "Hybrid/Electric",
];
export const ATTRACTION_FEATURES = [
  "Guided Tours",
  "Workshops",
  "Live Shows",
  "Exhibits",
];
export const ATTRACTION_INCLUDED = [
  "Wheelchair Accessible",
  "Kid-Friendly",
  "Pet-Friendly",
  "Food and Beverage",
];
export const SAFETY_MEASURES = ["On-Site Parking", "Nearby Parking", "None"];
export const TIMEZONES = [
  {
    offset: "GMT+01:00",
    name: "West Central Africa",
  },
  {
    offset: "GMT-12:00",
    name: "Etc/GMT-12",
  },
  {
    offset: "GMT-11:00",
    name: "Etc/GMT-11",
  },
  {
    offset: "GMT-11:00",
    name: "Pacific/Midway",
  },
  {
    offset: "GMT-10:00",
    name: "America/Adak",
  },
  {
    offset: "GMT-09:00",
    name: "America/Anchorage",
  },
  {
    offset: "GMT-09:00",
    name: "Pacific/Gambier",
  },
  {
    offset: "GMT-08:00",
    name: "America/Dawson_Creek",
  },
  {
    offset: "GMT-08:00",
    name: "America/Ensenada",
  },
  {
    offset: "GMT-08:00",
    name: "America/Los_Angeles",
  },
  {
    offset: "GMT-07:00",
    name: "America/Chihuahua",
  },
  {
    offset: "GMT-07:00",
    name: "America/Denver",
  },
  {
    offset: "GMT-06:00",
    name: "America/Belize",
  },
  {
    offset: "GMT-06:00",
    name: "America/Cancun",
  },
  {
    offset: "GMT-06:00",
    name: "America/Chicago",
  },
  {
    offset: "GMT-06:00",
    name: "Chile/EasterIsland",
  },
  {
    offset: "GMT-05:00",
    name: "America/Bogota",
  },
  {
    offset: "GMT-05:00",
    name: "America/Havana",
  },
  {
    offset: "GMT-05:00",
    name: "America/New_York",
  },
  {
    offset: "GMT-04:30",
    name: "America/Caracas",
  },
  {
    offset: "GMT-04:00",
    name: "America/Campo_Grande",
  },
  {
    offset: "GMT-04:00",
    name: "America/Glace_Bay",
  },
  {
    offset: "GMT-04:00",
    name: "America/Goose_Bay",
  },
  {
    offset: "GMT-04:00",
    name: "America/Santiago",
  },
  {
    offset: "GMT-04:00",
    name: "America/La_Paz",
  },
  {
    offset: "GMT-03:00",
    name: "America/Argentina/Buenos_Aires",
  },
  {
    offset: "GMT-03:00",
    name: "America/Montevideo",
  },
  {
    offset: "GMT-03:00",
    name: "America/Araguaina",
  },
  {
    offset: "GMT-03:00",
    name: "America/Godthab",
  },
  {
    offset: "GMT-03:00",
    name: "America/Miquelon",
  },
  {
    offset: "GMT-03:00",
    name: "America/Sao_Paulo",
  },
  {
    offset: "GMT-03:30",
    name: "America/St_Johns",
  },
  {
    offset: "GMT-02:00",
    name: "America/Noronha",
  },
  {
    offset: "GMT-01:00",
    name: "Atlantic/Cape_Verde",
  },
  {
    offset: "GMT",
    name: "Europe/Belfast",
  },
  {
    offset: "GMT",
    name: "Africa/Abidjan",
  },
  {
    offset: "GMT",
    name: "Europe/Dublin",
  },
  {
    offset: "GMT",
    name: "Europe/Lisbon",
  },
  {
    offset: "GMT",
    name: "Europe/London",
  },
  {
    offset: "UTC",
    name: "UTC",
  },
  {
    offset: "GMT+01:00",
    name: "Africa/Algiers",
  },
  {
    offset: "GMT+01:00",
    name: "Atlantic/Azores",
  },
  {
    offset: "GMT+01:00",
    name: "Atlantic/Stanley",
  },
  {
    offset: "GMT+01:00",
    name: "Europe/Amsterdam",
  },
  {
    offset: "GMT+01:00",
    name: "Europe/Belgrade",
  },
  {
    offset: "GMT+01:00",
    name: "Europe/Brussels",
  },
  {
    offset: "GMT+02:00",
    name: "Africa/Cairo",
  },
  {
    offset: "GMT+02:00",
    name: "Africa/Blantyre",
  },
  {
    offset: "GMT+02:00",
    name: "Asia/Beirut",
  },
  {
    offset: "GMT+02:00",
    name: "Asia/Damascus",
  },
  {
    offset: "GMT+02:00",
    name: "Asia/Gaza",
  },
  {
    offset: "GMT+02:00",
    name: "Asia/Jerusalem",
  },
  {
    offset: "GMT+03:00",
    name: "Africa/Addis_Ababa",
  },
  {
    offset: "GMT+03:00",
    name: "Asia/Riyadh89",
  },
  {
    offset: "GMT+03:00",
    name: "Europe/Minsk",
  },
  {
    offset: "GMT+03:30",
    name: "Asia/Tehran",
  },
  {
    offset: "GMT+04:00",
    name: "Asia/Dubai",
  },
  {
    offset: "GMT+04:00",
    name: "Asia/Yerevan",
  },
  {
    offset: "GMT+04:00",
    name: "Europe/Moscow",
  },
  {
    offset: "GMT+04:30",
    name: "Asia/Kabul",
  },
  {
    offset: "GMT+05:00",
    name: "Asia/Tashkent",
  },
  {
    offset: "GMT+05:30",
    name: "Asia/Kolkata",
  },
  {
    offset: "GMT+05:45",
    name: "Asia/Katmandu",
  },
  {
    offset: "GMT+06:00",
    name: "Asia/Dhaka",
  },
  {
    offset: "GMT+06:00",
    name: "Asia/Yekaterinburg",
  },
  {
    offset: "GMT+06:30",
    name: "Asia/Rangoon",
  },
  {
    offset: "GMT+07:00",
    name: "Asia/Bangkok",
  },
  {
    offset: "GMT+07:00",
    name: "Asia/Novosibirsk",
  },
  {
    offset: "GMT+08:00",
    name: "Etc/GMT+8",
  },
  {
    offset: "GMT+08:00",
    name: "Asia/Hong_Kong",
  },
  {
    offset: "GMT+08:00",
    name: "Asia/Krasnoyarsk",
  },
  {
    offset: "GMT+08:00",
    name: "Australia/Perth",
  },
  {
    offset: "GMT+08:45",
    name: "Australia/Eucla",
  },
  {
    offset: "GMT+09:00",
    name: "Asia/Irkutsk",
  },
  {
    offset: "GMT+09:00",
    name: "Asia/Seoul",
  },
  {
    offset: "GMT+09:00",
    name: "Asia/Tokyo",
  },
  {
    offset: "GMT+09:30",
    name: "Australia/Adelaide",
  },
  {
    offset: "GMT+09:30",
    name: "Australia/Darwin",
  },
  {
    offset: "GMT+09:30",
    name: "Pacific/Marquesas",
  },
  {
    offset: "GMT+10:00",
    name: "Etc/GMT+10",
  },
  {
    offset: "GMT+10:00",
    name: "Australia/Brisbane",
  },
  {
    offset: "GMT+10:00",
    name: "Australia/Hobart",
  },
  {
    offset: "GMT+10:00",
    name: "Asia/Yakutsk",
  },
  {
    offset: "GMT+10:30",
    name: "Australia/Lord_Howe",
  },
  {
    offset: "GMT+11:00",
    name: "Asia/Vladivostok",
  },
  {
    offset: "GMT+11:30",
    name: "Pacific/Norfolk",
  },
  {
    offset: "GMT+12:00",
    name: "Etc/GMT+12",
  },
  {
    offset: "GMT+12:00",
    name: "Asia/Anadyr",
  },
  {
    offset: "GMT+12:00",
    name: "Asia/Magadan",
  },
  {
    offset: "GMT+12:00",
    name: "Pacific/Auckland",
  },
  {
    offset: "GMT+12:45",
    name: "Pacific/Chatham",
  },
  {
    offset: "GMT+13:00",
    name: "Pacific/Tongatapu",
  },
  {
    offset: "GMT+14:00",
    name: "Pacific/Kiritimati",
  },
];
export const RECURRING_FREQUENCIES = [
  "Every day",
  "Every week",
  "Every two weeks",
  "Every month",
  "Every two months",
  "Every quarter",
  "Every 6 months",
  "Every year",
  "Every 2 years",
];
export const BUSINESSES = [
  {
    name: "Hotel",
    icon: "tabler:bed",
    link: PATH_DASHBOARD.addListings,
  },
  {
    name: "Event",
    icon: "material-symbols:event-outline",
    link: PATH_DASHBOARD.addListings,
  },
  {
    name: "Automobile",
    icon: "mdi:car-outline",
    link: PATH_DASHBOARD.addListings,
  },
  {
    name: "Short-let",
    icon: "material-symbols:villa-outline",
    link: PATH_DASHBOARD.addListings,
  },
  {
    name: "Cruise",
    icon: "icon-park-outline:cruise",
    link: PATH_DASHBOARD.addListings,
  },
  {
    name: "Buy/Sell",
    icon: "material-symbols:storefront-outline",
    link: PATH_DASHBOARD.addListings,
  },
  {
    name: "Attractions",
    icon: "material-symbols:event-outline",
    link: PATH_DASHBOARD.addListings,
  },
];
export const AMENITY_TYPES = [
  {
    label: "Workspace",
    value: "workspace",
    icon: "material-symbols:bed-outline",
  },
  {
    label: "Parking Area",
    value: "parking",
    icon: "material-symbols-light:apartment",
  },
  { label: "Breakfast", value: "breakfast", icon: "map:boat-tour" },
  { label: "Wifi", value: "wifi", icon: "ph:building" },
  {
    label: "Swimming pool",
    value: "swimming_pool",
    icon: "material-symbols:villa-outline",
  },
  {
    label: "Electricity",
    value: "electricity",
    icon: "fluent-mdl2:breakfast",
  },
  {
    label: "Housekeeping",
    value: "housekeeping",
    icon: "hugeicons:guest-house",
  },
  {
    label: "24hrs Reception",
    value: "reception",
    icon: "material-symbols:cabin",
  },
  {
    label: "TV",
    value: "tv",
    icon: "material-symbols:bungalow-outline",
  },
  { label: "GYM", value: "gym", icon: "mingcute:house-2-line" },
  { label: "Pet Services", value: "pet", icon: "mdi:farm-home-outline" },
  { label: "Bar", value: "bar", icon: "material-symbols:chalet-outline" },
];
export const VEHICLE_AMENITY_TYPES = [
  {
    label: "Air conditioning",
    value: "air-conditioning",
    icon: "mdi:air-conditioner",
  },
  {
    label: "GPS navigation",
    value: "gps",
    icon: "tdesign:gps",
  },
  {
    label: "BT/Audio System",
    value: "audio_systm",
    icon: "mdi:bluetooth-audio",
  },
  { label: "Child seat", value: "child_seat", icon: "mdi:car-child-seat" },
  {
    label: "Backup camera",
    value: "backup_camera",
    icon: "ph:webcam-duotone",
  },
  {
    label: "Sunroof",
    value: "sunroof",
    icon: "material-symbols-light:solar-power-outline",
  },
];
export const BOAT_AMENITY_TYPES = [
  {
    label: "Bedrooms",
    value: "bedrooms",
    icon: "mdi:bedroom-outline",
  },
  {
    label: "Sun Deck",
    value: "sun-deck",
    icon: "material-symbols-light:sunny-snowing",
  },
  {
    label: "Lounge Area",
    value: "lounge",
    icon: "carbon:drink-01",
  },
  {
    label: "Kitchen/Galley",
    value: "kitchen",
    icon: "material-symbols:soup-kitchen-outline",
  },
  {
    label: "Bathroom",
    value: "bathroom",
    icon: "cil:bathroom",
  },
  {
    label: "Entertainment ",
    value: "entertainment ",
    icon: "ri:music-fill",
  },
];
export const BOAT_SAFETY_FEATURES = [
  {
    label: "Life Jackets",
    value: "life-jacket",
    icon: "game-icons:life-jacket",
  },
  {
    label: "Fire Extinguishers",
    value: "fire-extinguishers",
    icon: "ph:fire-extinguisher",
  },
  {
    label: "First Aid Kit",
    value: "firstaid-kit",
    icon: "icon-park-twotone:first-aid-kit",
  },
  {
    label: "GPS/Navigation",
    value: "gps",
    icon: "jam:gps",
  },
  {
    label: "VHF Radio",
    value: "vhf-radio",
    icon: "carbon:radio-push-to-talk",
  },
  {
    label: "Anchor",
    value: "anchor",
    icon: "material-symbols-light:anchor",
  },
];

export const LISTING_AMENITY_TYPES = [
  {
    label: "Swimming pool",
    value: "swimming_pool",
    icon: "material-symbols:villa-outline",
  },
  {
    label: "Electricity",
    value: "electricity",
    icon: "fluent-mdl2:breakfast",
  },
  { label: "GYM", value: "gym", icon: "gg:gym" },
  {
    label: "Garden",
    value: "garden",
    icon: "guidance:garden",
  },
  {
    label: "Fireplace",
    value: "fireplace",
    icon: "game-icons:fireplace",
  },
  {
    label: "Balcony",
    value: "balcony",
    icon: "material-symbols-light:balcony",
  },
];
export const LAND_AMENITY_TYPES = [
  {
    label: "Water",
    value: "water",
    icon: "game-icons:tap",
  },
  {
    label: "Street light",
    value: "street_light",
    icon: "game-icons:double-street-lights",
  },
  { label: "Gas", value: "gas", icon: "mdi:gas" },
  {
    label: "Drainage system",
    value: "drainage",
    icon: "ph:pipe-duotone",
  },
  {
    label: "24/7 light",
    value: "light",
    icon: "mage:electricity",
  },
  {
    label: "Internet line",
    value: "internet",
    icon: "hugeicons:internet-antenna-04",
  },
  {
    label: "Playground",
    value: "playground",
    icon: "mdi:playground-seesaw",
  },
  {
    label: "Security",
    value: "security",
    icon: "mdi:security-lock",
  },
];
export const OTHER_AMENITIES = [
  "Shower",
  "Spa and Wellness Center",
  "Dining table",
  "Fitness center / GYM",
  "Dining area",
  "Coffee shop",
  "Toaster",
  "Bar and Lounge",
  "Cable channels",
  "on-site restaurant",
  "Kitchenette",
  "Business center",
  "Microwave",
  "Lobby and Reception",
  "Refridgerator",
  "Concierge Desk",
  "TV",
  "Towels",
  "Toilet",
  "Kid's Play Area",
  "Rooftop Terrace",
  "Sauna and Steam Room",
];
export const OTHER_BOAT_AMENITIES = [
  "Jacuzzi",
  "Swimming Platform",
  "Water Sports Gear",
  "Fishing Equipment",
  "BBQ Grill",
];
export const OTHER_AUTOMOBILE_INCLUDED = ["Clean cars"];
export const AUTOMOBILE_REQUIREMENTS = [
  "A valid passport or national ID card",
  "A valid driving licence",
  "A credit or debit card in the driver's name or other",
];
export const ZONING_TYPES = [
  "Residential",
  "Commercial",
  "Agricultural",
  "Industrial",
  "Mixed-Use",
];
export const LAND_FACILITIES = ["Paved", "Gravel", "Dirt", "Others"];
export const LAND_NEARBY_FACILITIES = [
  "Schools",
  "Hospitals",
  "Shopping store",
  "Public Transportation",
];
export const PAYSTACK_KEY = "pk_live_24f8b12630a68ec6f37f0bdb15599e3af80a0670";
export const MONNIFY_KEY = "MK_PROD_FYEHEG2ZVB";
export const STRIPE_KEY =
  "pk_live_51QcRtlEJAjApagQ5Uz9YgrBCnBsk80KHDNs4Abg1gzieKcDAHcKJpEQW1gv6e241kSIDKaQJHGCeB7Jhg6zZeqfx00NLPcqnKv";
export const CopyToBoard = (link, enqueueSnackbar, text = "") => {
  navigator.clipboard.writeText(link);
  enqueueSnackbar(text || "Link copied to clipboard.", {
    variant: "success",
  });
};
export const isValidEmail = (email) =>
  String(email)
    .toLowerCase()
    .match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    );
const convertMsToDays = (ms) => {
  const msInOneSecond = 1000;
  const secondsInOneMinute = 60;
  const minutesInOneHour = 60;
  const hoursInOneDay = 24;

  const minutesInOneDay = hoursInOneDay * minutesInOneHour;
  const secondsInOneDay = secondsInOneMinute * minutesInOneDay;
  const msInOneDay = msInOneSecond * secondsInOneDay;

  return Math.floor(ms / msInOneDay);
};
export const genLink = (type, uid, title) => {
  return `https://useafritas.com/${type}/${title
    .replaceAll(" ", "-")
    .toLowerCase()}-${uid}`;
};
export const getDaysBetweenDates = (dateOne, dateTwo) => {
  let differenceInMs = dateTwo.getTime() - dateOne.getTime();

  if (differenceInMs < 0) {
    differenceInMs = dateOne.getTime() - dateTwo.getTime();
  }

  return convertMsToDays(differenceInMs);
};
export const instanceId = "403498d8-d607-4791-b268-ac10b37928c9";
export const firebaseConfig = {
  apiKey: "AIzaSyDrQpp4Q_TqvZ7aS-oXW6cRI6IC7VV4o-M",
  authDomain: "afritas-9413a.firebaseapp.com",
  projectId: "afritas-9413a",
  storageBucket: "afritas-9413a.appspot.com",
  messagingSenderId: "553627276323",
  appId: "1:553627276323:web:7c29b697525ea46a8b8d96",
  measurementId: "G-54B01TWEF7",
  databaseURL: "https://afritas-9413a-default-rtdb.firebaseio.com",
};
export const GOOGLE = {
  clientId:
    "867966493030-0miu179o4mv5s35u141qksn3aif7s3il.apps.googleusercontent.com",
  clientSecret: "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",
};
export const mapKey = "AIzaSyCIoJOru4dY3Vx7vScp7X2M92eOYOuvOhw";
export const PAGE_DATA = [
  {
    path: PATH_DASHBOARD.root,
    permission: "view_dashboard_analytics",
    code: "0",
  },
  {
    path: PATH_DASHBOARD.general.insights,
    permission: "view_insights",
    code: "0",
  },
  { path: PATH_DASHBOARD.general.account, permission: "", code: "0" },
  { path: PATH_DASHBOARD.general.settings, permission: "", code: "0" },
  {
    path: PATH_DASHBOARD.general.preferences,
    permission: "view_preferences",
    code: "0",
  },
  {
    path: PATH_DASHBOARD.general.notification,
    permission: "view_notifications",
    code: "0",
  },
  {
    path: PATH_DASHBOARD.general.payment,
    permission: "view_payment_settings",
    code: "0",
  },
  {
    path: PATH_DASHBOARD.general.privacy,
    permission: "view_privacy_settings",
    code: "0",
  },
  {
    path: PATH_DASHBOARD.general.security,
    permission: "view_security_settings",
    code: "0",
  },
  {
    path: PATH_DASHBOARD.general.subscriptions,
    permission: "view_subscriptions",
    code: "0",
  },
  {
    path: PATH_DASHBOARD.bookings,
    permission: "view_bookings",
    code: "0",
  },
  {
    path: PATH_DASHBOARD.addBookings,
    permission: "add_bookings",
    code: "1",
  },
  {
    path: PATH_DASHBOARD.terminals,
    permission: "view_terminals",
    code: "1",
  },
  {
    path: PATH_DASHBOARD.terminalRequests,
    permission: "view_terminals",
    code: "1",
  },
  {
    path: PATH_DASHBOARD.requestTerminals,
    permission: "request_terminals",
    code: "1",
  },
  {
    path: PATH_DASHBOARD.listings,
    permission: "view_listings",
    code: "0",
  },
  {
    path: PATH_DASHBOARD.addListings,
    permission: "add_listings",
    code: "0",
  },
  {
    path: PATH_DASHBOARD.earnings,
    permission: "view_earnings",
    code: "0",
  },
  {
    path: PATH_DASHBOARD.staff,
    permission: "view_staff",
    code: "4",
  },
  {
    path: PATH_DASHBOARD.roles,
    permission: "view_staff_roles",
    code: "4",
  },
  {
    path: PATH_DASHBOARD.messages,
    permission: "view_messages",
    code: "0",
  },
  {
    path: PATH_DASHBOARD.wallet,
    permission: "view_wallet",
    code: "0",
  },
  {
    path: PATH_DASHBOARD.reviews,
    permission: "view_ratings_and_reviews",
    code: "0",
  },
  {
    path: PATH_DASHBOARD.insights,
    permission: "view_insights",
    code: "0",
  },
  {
    path: PATH_DASHBOARD.transactions,
    permission: "view_transactions",
    code: "6",
  },
  {
    path: PATH_DASHBOARD.logs,
    permission: "view_activity_logs",
    code: "0",
  },
  {
    path: PATH_DASHBOARD.calendar,
    permission: "view_calendar",
    code: "0",
  },
  {
    path: PATH_DASHBOARD.leads,
    permission: "view_property_leads",
    code: "0",
  },
  {
    path: PATH_DASHBOARD.tickets,
    permission: "view_bookings",
    code: "0",
  },
  {
    path: PATH_DASHBOARD.checkedIn,
    permission: "view_checkedin_bookings",
    code: "0",
  },
  {
    path: PATH_DASHBOARD.ticketTypes,
    permission: "view_bookings",
    code: "0",
  },
  {
    path: PATH_DASHBOARD.checkedIn,
    permission: "view_bookings",
    code: "0",
  },
  {
    path: PATH_DASHBOARD.addTicket,
    permission: "add_bookings",
    code: "1",
  },
  {
    path: PATH_DASHBOARD.referral,
    permission: "view_referrals",
    code: "11",
  },
  {
    path: PATH_DASHBOARD.reports,
    permission: "view_reports", // "view_referrals",
    code: "6",
  },
];
export const PAYOUT_TYPE = [
  { label: "All", value: "all" },
  {
    label: "Afritas Booking",
    value: "afritas",
  },
  {
    label: "Host Booking",
    value: "host",
  },
  {
    label: "Manual Payment",
    value: "manual",
  },
];
export const WALLET_TRANSACTION_TYPE = [
  {
    name: "Hotel",
    icon: "tabler:bed",
    narration: "Payment for",
  },
  {
    name: "Event",
    icon: "material-symbols:event-outline",
    narration: "Payment for",
  },
  {
    name: "Automobile",
    icon: "mdi:car-outline",
    narration: "Payment for",
  },
  {
    name: "Short-let",
    icon: "material-symbols:villa-outline",
    narration: "Payment for",
  },
  {
    name: "Cruise",
    icon: "icon-park-outline:cruise",
    narration: "Payment for",
  },
  {
    name: "Buy/Sell",
    icon: "material-symbols:storefront-outline",
    narration: "Payment for",
  },
  {
    name: "Attractions",
    icon: "material-symbols:event-outline",
    narration: "Payment for",
  },
  {
    name: "Wallet Debit",
    color: "info",
    value: "0",
    icon: "hugeicons:wallet-remove-02",
    narration: "Transfer to",
  },
  {
    name: "Wallet Credit",
    color: "success",
    value: "1",
    icon: "hugeicons:wallet-add-02",
    narration: "Wallet Topup",
  },
  {
    name: "Airtime",
    color: "success",
    value: "1",
    icon: "fluent:phone-28-regular",
    narration: "Airtime recharge",
  },
  {
    name: "Data",
    color: "success",
    value: "1",
    icon: "iconoir:internet",
    narration: "Data recharge",
  },
  {
    name: "Cable TV",
    color: "success",
    value: "1",
    icon: "material-symbols:tv-outline",
    narration: "Cable payment",
  },
  {
    name: "Electricity",
    color: "success",
    value: "1",
    icon: "ic:outline-power",
    narration: "Power payment",
  },
];
export const CheckPerm = (perm) => {
  const { user } = useAuth();
  const permissions =
    user && user.role && user.role.permissions && user.role.permissions !== ""
      ? JSON.parse(user.role.permissions)
      : [];
  if (perm.split(",").length > 0) {
    return perm.split(",").some((p) => permissions.includes(p));
  }
  return perm === "" || permissions.includes(perm);
};
export const CheckOnboardingDone = () => {
  const { user } = useAuth();
  let progress = 0;
  const progressIncrement = 10;
  let steps = {
    hostDetails: false,
    hostContact: false,
    hostPolicy: false,
    hostSocials: false,
    publishType: false,
    compliance: false,
    listing: false,
    wallet: false,
    payout: false,
    afritasBooking: false,
    hostBooking: false,
    team: false,
    subscription: false,
    referral: false,
  };
  try {
    const { subscription, host, onboarding, payoutAccount } = user;
    steps = {
      hostDetails: host && host.about,
      hostContact: host && host.businessLocation,
      hostPolicy: host && host.cancellationPolicy,
      hostSocials:
        host && (host.facebook || host.tiktok || host.youtube || host.twitter),
      publishType: user && Boolean(user.user && user.user.publishType),
      compliance:
        user && parseInt((user.user && user.user.idStatus) || 0, 10) === 1,
      listing: onboarding && parseInt(onboarding.isListingAdded || 0, 10) === 1,
      wallet: onboarding && parseInt(onboarding.isWalletAdded || 0, 10) === 1,
      payout: payoutAccount && payoutAccount.length > 0,
      afritasBooking:
        onboarding && parseInt(onboarding.isAfritasBooking || 0, 10) === 1,
      hostBooking:
        onboarding && parseInt(onboarding.isHostBooking || 0, 10) === 1,
      team: onboarding && parseInt(onboarding.isTeamAssigned || 0, 10) === 1,
      subscription: subscription && Object.keys(subscription).includes("plan"),
      referral: onboarding && parseInt(onboarding.isReferred || 0, 10) === 1,
    };
    if (steps.hostDetails && steps.hostContact && steps.hostPolicy) {
      progress += progressIncrement;
    }
    if (steps.publishType && steps.compliance) {
      progress += progressIncrement;
    }
    if (steps.listing) {
      progress += progressIncrement;
    }
    if (steps.wallet) {
      progress += progressIncrement;
    }
    if (user && user.notification) {
      const notification = JSON.parse(user.notification);
      if (notification.payout_method) {
        const isPayoutMethod = parseInt(notification.payout_method || 0, 10);
        if (
          isPayoutMethod === 0 ||
          (payoutAccount &&
            payoutAccount.length > 0 &&
            payoutAccount.filter((item) => item.currency === "NGN").length > 0)
        ) {
          steps.payout = true;
          progress += progressIncrement;
        }
      }
    }
    if (steps.afritasBooking) {
      progress += progressIncrement;
    }
    if (steps.hostBooking) {
      progress += progressIncrement;
    }
    if (steps.team) {
      progress += progressIncrement;
    }
    if (steps.subscription) {
      progress += progressIncrement;
    }
    if (steps.referral) {
      progress += progressIncrement;
    }
  } catch (err) {
    console.log(err);
  }

  return { progress, steps };
};
export const CheckSub = (action) => {
  /**
   * Sub response type
   * 0 - new user
   * 1 - active
   * 2 - cancelled.
   */
  const { user } = useAuth();
  const { subscription } = user;

  if (action === "" || action === "0") {
    // free features
    return 1;
  }

  if (subscription && Object.keys(subscription).includes("plan")) {
    const status = parseInt(subscription.status, 10);
    if (status === 1 || status === 2) {
      // active or cancelled subscription
      const features = subscription.features
        ? JSON.parse(subscription.features)
        : [];
      const isComplied =
        user &&
        user.compliance &&
        user.compliance.status &&
        parseInt(user.compliance.status, 10) === 1;
      if (features.includes(action)) {
        if ((action === "8" || action === "13") && !isComplied) {
          // check if action is sales or online sales
          return 2;
        }
        return 1;
      }
    }
    return 2;
  }
  // new user
  return 0;
};
export const handleGetEntry = (date, entries, defaultPrice, roomId, availableDays = []) => {
  let price = defaultPrice;
  let items = [];
  let customItems = [];
  let isOpen = true;
  let isBooked = false;
  let bookedQty = 0;
  let blockedQty = 0;
  let weekendDays = 0;
  let discount = 0;
  let totalBeforeDiscount = 0;
  let discountType = "";
  let weekendPrice = 0;
  let totalPrice = 0;
  let title = "";
  try {
    const startDate =
      (typeof (Array.isArray(date) ? date[0] : date) === "object" &&
        (Array.isArray(date) ? date[0] : date)) ||
      parse(Array.isArray(date) ? date[0] : date, "yyyy-MM-dd", new Date());

    const endDate =
      Array.isArray(date) && date.length > 1
        ? (typeof (Array.isArray(date) ? date[1] : date) === "object" &&
          (Array.isArray(date) ? date[1] : date)) ||
        parse(Array.isArray(date) ? date[1] : date, "yyyy-MM-dd", new Date())
        : startDate;

    // check if available days is empty, if empty, allow all, else, filter out days that are not in available days
    if (availableDays && availableDays.length > 0) {
      const isAvailable = availableDays.some((day) => {
        return format(startDate, 'EEEE').toLocaleLowerCase() === String(day).toLocaleLowerCase();
      });
      console.log({ availableDays, isAvailable });
      if (!isAvailable) {
        return { price, items, customItems, isOpen: false, isBooked, bookedQty, blockedQty: 1000000000, weekendDays, discount, totalBeforeDiscount, discountType, weekendPrice, totalPrice, title };
      }
    }

    items = entries.filter(
      (item) =>
        parseInt(roomId, 10) === parseInt(item.roomType, 10) &&
        (((isAfter(item.dateFrom, startDate) ||
          isSameDay(startDate, item.dateFrom)) &&
          (isBefore(item.dateTo, endDate) ||
            isSameDay(endDate, item.dateTo))) ||
          isWithinInterval(startDate, {
            start: parseISO(item.dateFrom),
            end: parseISO(item.dateTo),
          }) ||
          isWithinInterval(endDate, {
            start: parseISO(item.dateFrom),
            end: parseISO(item.dateTo),
          })),
    );
    customItems = entries.filter((item) => item.customType !== "");
    if (items.length > 0) {
      const availabilityItems = items.filter(
        (item) =>
          parseInt(item.type || 0, 10) === 0 ||
          parseInt(item.type || 0, 10) === 2,
      );
      if (availabilityItems.length > 0) {
        // availability
        let openedItem = availabilityItems.filter(
          (item) => parseInt(item.isOpened || 0, 10) !== 2,
        );
        if (openedItem.length > 0) {
          openedItem = openedItem[0];
          isOpen = parseInt(openedItem.isOpened || 0, 10) === 1;
          blockedQty = !isOpen ? parseInt(openedItem.qty || 0, 10) : 0;
        }

        // bookings
        bookedQty = availabilityItems
          .filter((item) => parseInt(item.isOpened || 0, 10) === 2)
          .reduce((total, prev) => (total += parseInt(prev.qty || 0, 10)), 0);
        isBooked = bookedQty > 0;
        isOpen = isBooked ? 0 : isOpen;
      }
    }
    if (customItems.length > 0) {
      // check for weekend and custom prices
      for (let i = 0; i < customItems.length; i += 1) {
        const item = customItems[i];
        const customType = parseInt(item.customType || 0, 10);
        const customAmount = parseFloat(item.customAmount || 0);

        const interval = fInterval(startDate, endDate) + 1;
        // check for weekly
        if (customType === 1 && interval > 6 && interval < 28) {
          // apply weekly
          discount = customAmount;
          discountType = "Weekly Discount";
        }

        // check for month
        if (customType === 2 && fInterval(startDate, endDate) >= 28) {
          // apply monthly
          discount = customAmount;
          discountType = "Monthly Discount";
        }

        // check for weekeend
        if (customType === 0) {
          weekendPrice = customAmount;
        }
      }
    }

    const totalDays = fInterval(startDate, endDate) || 1;
    let count = 0;
    console.log({ dante: totalDays });
    while (count < totalDays) {
      const date = new Date(new Date().setDate(startDate.getDate() + count));
      const day = date.getDay();
      if (weekendPrice > 0 && (day === 5 || day === 6 || day === 0)) {
        // is weekend, use weekend price
        totalBeforeDiscount += weekendPrice;
      } else {
        // use normal price
        const priceItems = items.filter(
          (item) =>
            parseInt(item.type || 0, 10) === 1 &&
            (isWithinInterval(date, {
              start: parseISO(item.dateFrom),
              end: parseISO(item.dateTo),
            }) ||
              (item.dateFrom === item.dateTo &&
                isSameDay(item.dateFrom, date))),
        );

        price = parseFloat(
          priceItems.length > 0 ? priceItems[0].baseAmount : defaultPrice,
        );
        totalBeforeDiscount += price;
      }
      count += 1;
    }
    totalPrice = Math.round(
      ((totalBeforeDiscount - (discount / 100) * totalBeforeDiscount) * 100) /
      100,
    );
  } catch (err) {
    console.log(err);
    price = defaultPrice;
  }

  return {
    title,
    price: price.toString(),
    isOpen,
    customItems,
    items,
    isBooked,
    bookedQty,
    blockedQty,
    unavailableQty: bookedQty + blockedQty,
    weekendDays,
    weekendPrice,
    totalPrice,
    totalBeforeDiscount,
    discount,
    discountType,
  };
};
export const humanFileSize = (bytes, si = true, dp = 1) => {
  const thresh = si ? 1000 : 1024;

  if (Math.abs(bytes) < thresh) {
    return bytes + " B";
  }
  const units = si
    ? ["kB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"]
    : ["KiB", "MiB", "GiB", "TiB", "PiB", "EiB", "ZiB", "YiB"];
  let u = -1;
  const r = 10 ** dp;

  do {
    bytes /= thresh;
    ++u;
  } while (
    Math.round(Math.abs(bytes) * r) / r >= thresh &&
    u < units.length - 1
  );
  return bytes.toFixed(dp) + " " + units[u];
};
export const verifyPhone = (guestPhone, guestCountry) => {
  let res = false;
  try {
    const phoneLength =
      (guestPhone &&
        guestCountry &&
        COUNTRIES.filter((country) => country.name === guestCountry).length >
        0 &&
        COUNTRIES.filter((country) => country.name === guestCountry)[0]
          .phoneLength) ||
      "";
    let minLength = 0;
    let maxLength = 0;
    if (Array.isArray(phoneLength)) {
      minLength = phoneLength[0];
      maxLength = phoneLength[1];
    } else {
      minLength = phoneLength;
      maxLength = phoneLength;
    }
    if (minLength > 0 && maxLength > 0 && guestPhone.length > 0) {
      res =
        isNumeric(guestPhone) &&
        guestPhone.length >= minLength &&
        guestPhone.length <= maxLength;
    }
  } catch (err) {
    console.log({ err });
  }
  return res;
};
